
import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { BackendQuestion, QuestionDifficulty, CertificationType, BackendOption } from '../../types';
import { 
    Plus, Edit, Trash2, Search, Filter, Save, X, 
    ChevronLeft, ChevronRight, Loader2, RefreshCcw, 
    Database, Sparkles, Download, Upload, FileJson,
    Wand2, Brain, AlertCircle, CheckCircle, Info, Tag,
    ListOrdered, MapPin, CheckSquare, Square,
    AlertTriangle, FileWarning, ShieldCheck, Activity,
    Settings2, LayoutGrid, Zap, Microscope, Server,
    HardDrive, Eraser, List, ArrowRight, ListTodo,
    Menu, ChevronDown, Check, Settings, Award
} from 'lucide-react';
import { Type } from "@google/genai";
import { StorageService } from '../../services/storageService';
import { PageLoader } from '../../components/PageLoader';
import { CNA_DOMAINS, US_STATES } from '../../data/mockData';
import { useAuth } from '../../components/AuthBridge';
import { PromptService } from '../../services/promptService';
import { runAiTask, generateCategorySuggestions, randomizeQuestionOptions } from '../../services/geminiService';
import { useThemeTokens } from '../../hooks/useActiveTheme';

// Fallback domain maps strictly separated by certification type
const DEFAULT_CERT_DOMAINS: Record<string, string[]> = {
    cna: CNA_DOMAINS,
    phlebotomy: ['Venipuncture', 'Specimen Handling', 'Safety & Compliance', 'Order of Draw', 'Infection Control', 'Patient Identification'],
    medical_assistant: ['Clinical Procedures', 'Pharmacology', 'Administrative Protocols', 'Vital Signs & Patient Care', 'Laboratory Procedures', 'Medical Terminology'],
    pharmacy_tech: ['Dosage Calculations', 'Medication Safety', 'Sterile Compounding', 'Federal Regulations', 'Pharmacology Basics', 'Order Entry & Processing'],
    pct: ['Advanced Patient Care', 'EKG Monitoring', 'Phlebotomy Fundamentals', 'Infection Control', 'Hemodynamics', 'Activities of Daily Living']
};

export const QuestionManager: React.FC = () => {
    const { user } = useAuth();
    const tokens = useThemeTokens();
    const isSuperAdmin = user?.permissions?.includes('*');
    
    const [certId, setCertId] = useState('cna');
    const [certs, setCerts] = useState<CertificationType[]>([]);
    const [questions, setQuestions] = useState<BackendQuestion[]>([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [limit, setLimit] = useState(20);
    const [selectedCategory, setSelectedCategory] = useState('All');
    const [selectedDifficulty, setSelectedDifficulty] = useState<string>('All');
    const [selectedVendor, setSelectedVendor] = useState<string>('All');
    const [dbCategories, setDbCategories] = useState<string[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [activeCollection, setActiveCollection] = useState<string>('pending...');
    
    // Selection & Edit state
    const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
    const [isBulkLoading, setIsBulkLoading] = useState(false);
    const [isExporting, setIsExporting] = useState(false);
    const [editingQuestion, setEditingQuestion] = useState<BackendQuestion | null>(null);
    const [isSavingEdit, setIsSavingEdit] = useState(false);

    // Category Management State
    const [isCategoryModalOpen, setIsCategoryModalOpen] = useState(false);
    const [isSuggestingCategories, setIsSuggestingCategories] = useState(false);

    // AI Generation State
    const [isAiModalOpen, setIsAiModalOpen] = useState(false);
    const [isAiGenerating, setIsAiGenerating] = useState(false);
    const [stagedAiQuestions, setStagedAiQuestions] = useState<BackendQuestion[]>([]);
    const [aiParams, setAiParams] = useState({
        count: 5,
        category: 'Basic Nursing Skills',
        difficulty: 'Medium' as QuestionDifficulty,
        state: 'Global'
    });

    const [status, setStatus] = useState<{type: 'error' | 'success' | 'warning' | 'info', text: string} | null>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);

    // AI-Assisted Batch Migrator dashboard states
    const [activeTab, setActiveTab] = useState<'bank' | 'migrator'>('bank');
    const [migratorStats, setMigratorStats] = useState<any>(null);
    const [isStatsLoading, setIsStatsLoading] = useState(false);
    const [migratorStyle, setMigratorStyle] = useState<'Prometric' | 'Credentia' | 'Headmaster'>('Prometric');
    
    // Side by side modal state
    const [activeDiffItem, setActiveDiffItem] = useState<{original: BackendQuestion, upgraded: BackendQuestion} | null>(null);
    const [isMigratingItem, setIsMigratingItem] = useState(false);
    
    // Batch progress tracking
    const [isBatchMigrating, setIsBatchMigrating] = useState(false);
    const [batchProgress, setBatchProgress] = useState<{current: number, total: number, log: string[]} | null>(null);

    // Single Question AI Modify state
    const [singleVendorStyle, setSingleVendorStyle] = useState<'Credentia' | 'Headmaster' | 'Prometric' | 'Normal'>('Credentia');
    const [singleCustomInstruction, setSingleCustomInstruction] = useState('');
    const [isAiModifyingSingle, setIsAiModifyingSingle] = useState(false);
    const [singleAiPreview, setSingleAiPreview] = useState<BackendQuestion | null>(null);

    // Bulk Questions AI Modify state
    const [isBulkAiModalOpen, setIsBulkAiModalOpen] = useState(false);
    const [bulkVendorStyle, setBulkVendorStyle] = useState<'Credentia' | 'Headmaster' | 'Prometric' | 'Normal'>('Credentia');
    const [bulkCustomInstruction, setBulkCustomInstruction] = useState('');
    const [isBulkAiTransforming, setIsBulkAiTransforming] = useState(false);
    const [isBulkAiSaving, setIsBulkAiSaving] = useState(false);
    const [stagedBulkItems, setStagedBulkItems] = useState<Array<{ original: BackendQuestion; upgraded: BackendQuestion; selected: boolean }>>([]);

    const loadMigratorStats = useCallback(async () => {
        setIsStatsLoading(true);
        try {
            const stats = await StorageService.getQuestionDifficultyStats(certId);
            setMigratorStats(stats);
        } catch (e) {
            console.error("Failed to load difficulty stats:", e);
        } finally {
            setIsStatsLoading(false);
        }
    }, [certId]);

    useEffect(() => {
        if (activeTab === 'migrator') {
            loadMigratorStats();
        }
    }, [activeTab, loadMigratorStats]);

    const handleSingleEnvisionUpgrade = async (q: BackendQuestion) => {
        setIsLoading(true);
        setStatus(null);
        try {
            const res = await StorageService.migrateQuestionAi(certId, q._id, migratorStyle);
            setActiveDiffItem({
                original: res.original,
                upgraded: res.upgraded
            });
        } catch (e: any) {
            setStatus({ type: 'error', text: `AI Envision upgrade failed: ${e.message}` });
        } finally {
            setIsLoading(false);
        }
    };

    const handleSaveUpgradedQuestion = async () => {
        if (!activeDiffItem) return;
        setIsMigratingItem(true);
        try {
            await StorageService.updateQuestion(certId, activeDiffItem.upgraded._id, activeDiffItem.upgraded);
            setStatus({ type: 'success', text: `Question successfully upgraded to ${migratorStyle} Hard standard.` });
            setActiveDiffItem(null);
            loadQuestions();
            loadMigratorStats();
        } catch (e: any) {
            alert(`Failed to save: ${e.message}`);
        } finally {
            setIsMigratingItem(false);
        }
    };

    const handleBatchMigrateSelected = async () => {
        if (selectedIds.size === 0) return;
        const itemsToMigrate = questions.filter(q => selectedIds.has(q._id) && q.difficulty !== 'Hard');
        if (itemsToMigrate.length === 0) {
            setStatus({ type: 'warning', text: 'No Easy or Medium questions selected for migration.' });
            return;
        }

        const confirmBatch = window.confirm(`Initiate state-grade AI upgrade for ${itemsToMigrate.length} selected items? This will revamp them to ${migratorStyle} standards.`);
        if (!confirmBatch) return;

        setIsBatchMigrating(true);
        setBatchProgress({
            current: 0,
            total: itemsToMigrate.length,
            log: [`Initializing automated batch migration to ${migratorStyle} standard...`]
        });

        let currentCount = 0;
        for (const item of itemsToMigrate) {
            currentCount++;
            setBatchProgress(prev => {
                if (!prev) return null;
                return {
                    ...prev,
                    current: currentCount,
                    log: [...prev.log, `[${currentCount}/${itemsToMigrate.length}] Upgrading: "${item.text.substring(0, 45)}..."`]
                };
            });

            try {
                // Call Migrate API
                const res = await StorageService.migrateQuestionAi(certId, item._id, migratorStyle);
                // Call Save API
                await StorageService.updateQuestion(certId, res.upgraded._id, res.upgraded);
                
                setBatchProgress(prev => {
                    if (!prev) return null;
                    return {
                        ...prev,
                        log: [...prev.log, `✅ Successfully upgraded & committed to registry.`]
                    };
                });
            } catch (err: any) {
                setBatchProgress(prev => {
                    if (!prev) return null;
                    return {
                        ...prev,
                        log: [...prev.log, `❌ Failed: ${err.message}`]
                    };
                });
            }
        }

        setBatchProgress(prev => {
            if (!prev) return null;
            return {
                ...prev,
                log: [...prev.log, `🎉 Batch upgrade complete. Refreshing telemetry...`]
            };
        });

        setSelectedIds(new Set());
        loadQuestions();
        loadMigratorStats();
        setTimeout(() => {
            setIsBatchMigrating(false);
            setBatchProgress(null);
            setStatus({ type: 'success', text: `Batch Migration completed successfully: ${itemsToMigrate.length} items processed.` });
        }, 3000);
    };

    const activeCert = certs.find(c => c.id === certId);

    // Active categories strictly synced with Cert Manager for the selected certification
    const activeCategories = useMemo(() => {
        const certCats = (activeCert?.categories || []).map(c => c.trim()).filter(Boolean);
        if (certCats.length > 0) {
            return certCats;
        }
        const defaultCats = DEFAULT_CERT_DOMAINS[certId.toLowerCase()];
        if (defaultCats && defaultCats.length > 0) {
            return defaultCats;
        }
        return certId.toLowerCase() === 'cna' ? CNA_DOMAINS : [];
    }, [activeCert, certId]);

    const loadCategories = useCallback(async () => {
        try {
            const certManagerCats = (activeCert?.categories || []).map(c => c.trim()).filter(Boolean);

            // 1. If Cert Manager has configured categories for this certification, use them directly as the source of truth
            if (certManagerCats.length > 0) {
                const canonicalCats = Array.from(new Set(certManagerCats)).sort((a, b) => a.localeCompare(b));
                setDbCategories(canonicalCats);
                return;
            }

            // 2. Otherwise query distinct categories from this cert's specific question collection
            const res = await StorageService.getQuestionCategories(certId);
            const fetchedCats = (res?.categories || [])
                .map((c: any) => (typeof c === 'string' ? c : c?.category || '').trim())
                .filter(Boolean);

            const catMap = new Map<string, string>();
            fetchedCats.forEach(cat => {
                const key = cat.toLowerCase();
                if (!catMap.has(key)) catMap.set(key, cat);
            });

            // 3. Fallback ONLY if both Cert Manager and DB have 0 categories for this cert type
            if (catMap.size === 0) {
                const fallbackList = DEFAULT_CERT_DOMAINS[certId.toLowerCase()] || (certId.toLowerCase() === 'cna' ? CNA_DOMAINS : []);
                fallbackList.forEach(cat => {
                    const key = cat.toLowerCase();
                    if (!catMap.has(key)) catMap.set(key, cat);
                });
            }

            const available = Array.from(catMap.values()).sort((a, b) => a.localeCompare(b));
            setDbCategories(available);
        } catch (e) {
            const certManagerCats = (activeCert?.categories || []).map(c => c.trim()).filter(Boolean);
            const fallbackList = certManagerCats.length > 0
                ? certManagerCats
                : (DEFAULT_CERT_DOMAINS[certId.toLowerCase()] || (certId.toLowerCase() === 'cna' ? CNA_DOMAINS : []));
            setDbCategories([...fallbackList].sort((a, b) => a.localeCompare(b)));
        }
    }, [certId, activeCert]);

    useEffect(() => {
        loadCategories();
    }, [loadCategories]);

    // Reset category filter and pagination whenever the active certification changes
    useEffect(() => {
        setSelectedCategory('All');
        setPage(1);
    }, [certId]);

    // Ensure selectedCategory is either 'All' or one of the available categories
    useEffect(() => {
        if (selectedCategory !== 'All' && dbCategories.length > 0 && !dbCategories.includes(selectedCategory)) {
            setSelectedCategory('All');
        }
    }, [dbCategories, selectedCategory]);

    useEffect(() => {
        if (activeCategories.length > 0) {
            setAiParams(prev => ({
                ...prev,
                category: activeCategories[0]
            }));
        }
    }, [activeCategories]);

    useEffect(() => {
        const timer = setTimeout(() => setDebouncedSearch(searchTerm), 500);
        return () => clearTimeout(timer);
    }, [searchTerm]);

    useEffect(() => {
        const fetchCerts = () => {
            StorageService.getCertifications(true).then(data => {
                if (data && data.length > 0) {
                    setCerts(data);
                    if (certId !== 'cna' && !data.find(c => c.id === certId)) {
                        setCertId(data[0].id);
                    }
                }
            }).catch(err => {
                console.warn("Could not refresh certs registry:", err);
            });
        };

        fetchCerts();

        const handleCertsUpdated = () => {
            fetchCerts();
        };
        window.addEventListener('certifications-updated', handleCertsUpdated);
        return () => window.removeEventListener('certifications-updated', handleCertsUpdated);
    }, []);

    const loadQuestions = useCallback(async () => {
        setIsLoading(true);
        try {
            loadCategories();
            const res = await StorageService.getQuestions(certId, { 
                page, 
                limit, 
                search: debouncedSearch.trim() || undefined,
                category: selectedCategory === 'All' ? undefined : selectedCategory,
                difficulty: selectedDifficulty === 'All' ? undefined : selectedDifficulty,
                vendor: selectedVendor === 'All' ? undefined : selectedVendor
            });
            
            if (res) {
                setQuestions(res.items || []);
                setTotal(res.total || 0);
                if (res.collection) setActiveCollection(res.collection);
            }
        } catch (e: any) {
            console.error("Registry Sync Error:", e);
            setStatus({ type: 'error', text: 'Registry Sync Error: Data unreachable. ' + e.message });
        } finally {
            setIsLoading(false);
        }
    }, [certId, page, limit, debouncedSearch, selectedCategory, selectedDifficulty, selectedVendor, loadCategories]);

    useEffect(() => { loadQuestions(); }, [loadQuestions]);

    const clearFilters = () => {
        setSearchTerm('');
        setSelectedCategory('All');
        setSelectedDifficulty('All');
        setSelectedVendor('All');
        setPage(1);
    };

    const handleSaveEdit = async () => {
        if (!editingQuestion) return;
        setIsSavingEdit(true);
        try {
            await StorageService.updateQuestion(certId, editingQuestion._id, editingQuestion);
            setStatus({ type: 'success', text: 'Logic node updated successfully.' });
            setEditingQuestion(null);
            setSingleAiPreview(null);
            loadQuestions();
        } catch (e) {
            setStatus({ type: 'error', text: 'Update failed: Check registry connection.' });
        } finally {
            setIsSavingEdit(false);
        }
    };

    const handleAiModifySingleQuestion = async () => {
        if (!editingQuestion) return;
        setIsAiModifyingSingle(true);
        setStatus(null);
        try {
            const res = await StorageService.migrateQuestionAi(
                certId, 
                editingQuestion, 
                singleVendorStyle, 
                singleCustomInstruction
            );
            if (res && res.upgraded) {
                setSingleAiPreview({
                    ...res.upgraded,
                    _id: editingQuestion._id // Maintain exact original ID
                });
                setStatus({ type: 'success', text: `Generated AI revision tailored for ${singleVendorStyle} CNA standards.` });
            }
        } catch (e: any) {
            console.error("Single AI Modify Error:", e);
            setStatus({ type: 'error', text: 'AI Modify failed: ' + (e.message || 'Unknown error') });
        } finally {
            setIsAiModifyingSingle(false);
        }
    };

    const handleApplySingleAiRevision = () => {
        if (!singleAiPreview || !editingQuestion) return;
        setEditingQuestion({
            ...singleAiPreview,
            _id: editingQuestion._id // Maintain exact original ID
        });
        setSingleAiPreview(null);
        setStatus({ type: 'success', text: 'AI Revision applied to Node Editor! Click "Commit Logic" to save to database.' });
    };

    const handleRunBulkAiTransformation = async () => {
        const ids = Array.from(selectedIds);
        if (ids.length === 0) return;
        setIsBulkAiTransforming(true);
        setStatus(null);
        try {
            const res = await StorageService.migrateQuestionsBulkAi(
                certId,
                ids,
                bulkVendorStyle,
                bulkCustomInstruction,
                false // autoCommit=false so admin previews first
            );
            
            if (res && res.items) {
                const mapped = res.items.map((item: any) => ({
                    original: item.original,
                    upgraded: {
                        ...item.upgraded,
                        _id: item.original._id // Maintain exact original ID
                    },
                    selected: true
                }));
                setStagedBulkItems(mapped);
                setStatus({ type: 'success', text: `Transformed ${mapped.length} questions into ${bulkVendorStyle} CNA format. Review and commit below.` });
            }
        } catch (e: any) {
            console.error("Bulk AI Transformation Error:", e);
            setStatus({ type: 'error', text: 'Bulk AI Transformation failed: ' + (e.message || 'Unknown error') });
        } finally {
            setIsBulkAiTransforming(false);
        }
    };

    const handleCommitBulkAiTransformed = async () => {
        const itemsToSave = stagedBulkItems.filter(i => i.selected).map(i => i.upgraded);
        if (itemsToSave.length === 0) return;
        setIsBulkAiSaving(true);
        try {
            for (const item of itemsToSave) {
                await StorageService.updateQuestion(certId, item._id, item);
            }
            setStatus({ type: 'success', text: `Successfully updated and committed ${itemsToSave.length} questions to registry!` });
            setIsBulkAiModalOpen(false);
            setStagedBulkItems([]);
            setSelectedIds(new Set());
            loadQuestions();
        } catch (e: any) {
            console.error("Failed to commit bulk AI updates:", e);
            setStatus({ type: 'error', text: 'Commit failed: ' + (e.message || 'Error') });
        } finally {
            setIsBulkAiSaving(false);
        }
    };

    const handleGenerateAI = async () => {
        setIsAiGenerating(true);
        setStatus(null);
        const prompts = PromptService.getPrompts();
        const activeCertName = activeCert?.name || 'Nursing Assistant';
        
        const stateContext = aiParams.state !== 'Global' 
            ? `Follow specific board protocols for the state of ${aiParams.state}.` 
            : `Follow general national standards for nursing care.`;

        // Support generating more than 15 questions (up to 50 items) via parallel batching
        const totalRequested = Math.max(1, Math.min(Number(aiParams.count) || 5, 50));
        const BATCH_SIZE = 15;
        const batchSizes: number[] = [];
        let remaining = totalRequested;
        while (remaining > 0) {
            const chunk = Math.min(remaining, BATCH_SIZE);
            batchSizes.push(chunk);
            remaining -= chunk;
        }

        if (batchSizes.length > 1) {
            setStatus({ 
                type: 'info', 
                text: `Synthesizing ${totalRequested} items in ${batchSizes.length} concurrent AI pipelines...` 
            });
        }

        try {
            const generateBatch = async (batchCount: number, bIdx: number) => {
                const response = await runAiTask(
                    `GENERATE ${batchCount} high-fidelity board-style questions for the ${activeCertName} certification.
                     
                     DOMAIN: "${aiParams.category}"
                     RIGOR LEVEL: "${aiParams.difficulty}"
                     STATE CONTEXT: ${stateContext}
                     
                     STRICT CLINICAL REQUIREMENTS:
                     1. Format: Multiple choice with exactly 4 options per question.
                     2. Correctness: Exactly one option must be marked as isCorrect: true.
                     3. OPTION ORDER & RANDOMIZATION: The correct answer (isCorrect: true) MUST be varied and distributed across positions A, B, C, and D. Do NOT always place the correct answer as Option A.
                     4. Distractors: Provide 3 clinically plausible, realistic distractors representing common candidate misconceptions or safety traps.
                     5. Explanation: Provide a detailed clinical rationale explaining the correct action based on board safety standards.
                     6. IDs: Do not generate _id fields, the system will assign them.`,
                    prompts.factory,
                    'gemini-3.1-pro-preview',
                    'application/json',
                    {
                        type: Type.ARRAY,
                        items: {
                            type: Type.OBJECT,
                            properties: {
                                text: { type: Type.STRING, description: 'The question stem' },
                                category: { type: Type.STRING, description: 'Domain classification' },
                                difficulty: { type: Type.STRING, enum: ['Easy', 'Medium', 'Hard'] },
                                explanation: { type: Type.STRING, description: 'Clinical rationale' },
                                options: {
                                    type: Type.ARRAY,
                                    items: {
                                        type: Type.OBJECT,
                                        properties: {
                                            text: { type: Type.STRING },
                                            isCorrect: { type: Type.BOOLEAN }
                                        },
                                        required: ['text', 'isCorrect']
                                    }
                                }
                            },
                            required: ['text', 'category', 'difficulty', 'explanation', 'options']
                        }
                    }
                );

                try {
                    return JSON.parse(response.text || '[]');
                } catch (err) {
                    console.warn(`Failed to parse AI batch ${bIdx + 1}:`, err);
                    return [];
                }
            };

            const batchResults = await Promise.all(
                batchSizes.map((size, idx) => generateBatch(size, idx))
            );

            const parsed: any[] = batchResults.flat();
            if (parsed.length === 0) {
                throw new Error("No questions were returned by the AI synthesis engine. Please try again.");
            }

            // Distribute correct answers across A, B, C, D so Option A is never the sole answer
            const finalized = parsed.map((q, idx) => {
                const targetSlot = (idx + Math.floor(Math.random() * 4)) % (q.options?.length || 4);
                const randomizedOptions = randomizeQuestionOptions(q.options || [], targetSlot);

                return {
                    ...q,
                    _id: `ai_staged_${Date.now()}_${idx}_${Math.random().toString(36).substring(2, 6)}`,
                    category: aiParams.category,
                    difficulty: aiParams.difficulty,
                    stateSpecificTags: aiParams.state === 'Global' ? [] : [aiParams.state],
                    vendorTags: ['Global'],
                    options: randomizedOptions.map((o: any, oIdx: number) => ({
                        ...o,
                        _id: `opt_${Date.now()}_${idx}_${oIdx}`
                    }))
                };
            });

            setStagedAiQuestions(prev => prev.length > 0 ? [...prev, ...finalized] : finalized);
            setStatus({ 
                type: 'success', 
                text: `Engine synthesized ${finalized.length} items with randomized answer positions across A-D. Review before publishing.` 
            });
        } catch (e: any) {
            console.error("AI Generation Error:", e);
            setStatus({ type: 'error', text: 'AI Synthesis Protocol Failure: ' + (e.message || 'Unknown Error') });
        } finally {
            setIsAiGenerating(false);
        }
    };

    const handlePublishStaged = async () => {
        if (stagedAiQuestions.length === 0) return;
        setIsAiGenerating(true);
        try {
            const cleanQuestions = stagedAiQuestions.map(({ _id, ...rest }) => rest);
            await StorageService.saveQuestionsBulk(certId, cleanQuestions as any);
            setStatus({ type: 'success', text: `Published ${cleanQuestions.length} items to ${certId.toUpperCase()} registry.` });
            setStagedAiQuestions([]);
            setIsAiModalOpen(false);
            loadQuestions();
        } catch (e) {
            setStatus({ type: 'error', text: 'Registry Commit Interrupted.' });
        } finally {
            setIsAiGenerating(false);
        }
    };

    const handleExport = async () => {
        setIsExporting(true);
        setStatus({ type: 'success', text: 'Preparing registry export package...' });
        try {
            const res = await StorageService.getQuestions(certId, { limit: 10000 });
            const dataStr = JSON.stringify(res.items, null, 2);
            const dataUri = 'data:application/json;charset=utf-8,'+ encodeURIComponent(dataStr);
            const exportFileDefaultName = `${certId}_questions_export_${new Date().toISOString().split('T')[0]}.json`;
            const linkElement = document.createElement('a');
            linkElement.setAttribute('href', dataUri);
            linkElement.setAttribute('download', exportFileDefaultName);
            linkElement.click();
            setStatus({ type: 'success', text: `Exported ${res.items.length} items from ${certId.toUpperCase()} registry.` });
        } catch (e) {
            setStatus({ type: 'error', text: 'Export Protocol Failed.' });
        } finally {
            setIsExporting(false);
        }
    };

    const handleImportFile = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setIsBulkLoading(true);
        setStatus({ type: 'warning', text: 'Reading logic file...' });
        const reader = new FileReader();
        reader.onload = async (event) => {
            try {
                const text = event.target?.result as string;
                if (!text) throw new Error("File empty");
                const items = JSON.parse(text);
                const result = await StorageService.saveQuestionsBulk(certId, Array.isArray(items) ? items : [items]);
                setStatus({ type: 'success', text: `Registry Sync Success: ${result.count} items committed.` });
                setTimeout(() => loadQuestions(), 1000);
            } catch (err: any) {
                setStatus({ type: 'error', text: `Sync Error: ${err.message}` });
            } finally {
                setIsBulkLoading(false);
            }
        };
        reader.readAsText(file);
        if (fileInputRef.current) fileInputRef.current.value = '';
    };

    const toggleSelectAll = () => {
        if (selectedIds.size === questions.length) setSelectedIds(new Set());
        else setSelectedIds(new Set(questions.map(q => q._id)));
    };

    const toggleSelect = (id: string) => {
        const next = new Set(selectedIds);
        if (next.has(id)) next.delete(id);
        else next.add(id);
        setSelectedIds(next);
    };

    const handleBulkDelete = async () => {
        if (selectedIds.size === 0) return;
        const confirmPurge = window.confirm(`CRITICAL ACTION: Purge ${selectedIds.size} items from the ${certId.toUpperCase()} registry? This action is permanent and cannot be undone.`);
        if (!confirmPurge) return;

        setIsBulkLoading(true);
        setStatus({ type: 'warning', text: 'Executing registry purge...' });
        try {
            const result = await StorageService.deleteQuestionsBulk(certId, Array.from(selectedIds));
            setStatus({ type: 'success', text: `Registry Sync: ${result.count || selectedIds.size} logic nodes purged successfully from ${certId.toUpperCase()}.` });
            setSelectedIds(new Set());
            loadQuestions();
        } catch (e: any) {
            setStatus({ type: 'error', text: `Purge protocol failed: ${e.message}` });
        } finally {
            setIsBulkLoading(false);
        }
    };

    const handleAiSuggestCategories = async () => {
        if (!activeCert) return;
        setIsSuggestingCategories(true);
        try {
            const suggestions = await generateCategorySuggestions(activeCert.name);
            const existing = activeCert.categories || [];
            const combined = Array.from(new Set([...existing, ...suggestions]));
            const updated = await StorageService.saveCertification({ ...activeCert, categories: combined });
            setCerts(prev => prev.map(c => c.id === certId ? updated : c));
            setStatus({ type: 'success', text: 'AI suggested domains integrated into the registry.' });
        } catch (e) {
            setStatus({ type: 'error', text: 'AI Category suggestion failed.' });
        } finally {
            setIsSuggestingCategories(false);
        }
    };

    const handleAddCategory = () => {
        const cat = prompt("Enter new category name:");
        if (cat && cat.trim() && activeCert) {
            const current = activeCert.categories || [];
            if (!current.includes(cat.trim())) {
                const combined = [...current, cat.trim()];
                StorageService.saveCertification({ ...activeCert, categories: combined }).then(updated => {
                    setCerts(prev => prev.map(c => c.id === certId ? updated : c));
                });
            }
        }
    };

    const handleRemoveCategory = (cat: string) => {
        if (activeCert && confirm(`Remove "${cat}" from registry?`)) {
            const current = activeCert.categories || [];
            const filtered = current.filter(c => c !== cat);
            StorageService.saveCertification({ ...activeCert, categories: filtered }).then(updated => {
                setCerts(prev => prev.map(c => c.id === certId ? updated : c));
            });
        }
    };

    const totalPages = Math.ceil(total / limit);

    return (
        <div className="animate-fade-in h-full flex flex-col pb-12 text-slate-900 relative">
            <div className="flex flex-col lg:flex-row justify-between items-start lg:items-end mb-8 gap-6">
                <div className="space-y-2">
                    <div className="flex items-center gap-3">
                        <h2 className="text-3xl font-black text-slate-900">
                            Registry Bank <span className="bg-indigo-50 text-indigo-600 px-3 py-1 rounded-full text-xs font-bold">{total} Items</span>
                        </h2>
                    </div>
                    {isSuperAdmin && (
                        <div className="flex items-center gap-2 px-2.5 py-1 bg-slate-900 text-white rounded-lg w-fit shadow-xl border border-white/10 group cursor-default">
                            <Server size={10} className="text-medical-400" />
                            <span className="text-[8px] font-black tracking-widest opacity-60">Path:</span>
                            <span className="text-[9px] font-black text-medical-400 tracking-tighter">{activeCollection}</span>
                        </div>
                    )}
                </div>
                <div className="flex flex-wrap gap-2">
                    {selectedIds.size > 0 && (
                        <>
                            <button 
                                onClick={handleBulkDelete}
                                disabled={isBulkLoading}
                                className="px-5 py-3.5 bg-rose-600 text-white rounded-2xl font-black text-[9px] tracking-widest flex items-center gap-2 shadow-xl border-b-4 border-rose-800 active:scale-95 transition-all animate-pop"
                            >
                                {isBulkLoading ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />} Purge {selectedIds.size}
                            </button>
                            <button 
                                onClick={() => { setIsBulkAiModalOpen(true); setStagedBulkItems([]); setStatus(null); }}
                                disabled={isBulkLoading}
                                className="px-5 py-3.5 bg-gradient-to-r from-amber-500 to-indigo-600 text-white rounded-2xl font-black text-[9px] tracking-widest flex items-center gap-2 shadow-xl border-b-4 border-amber-800 active:scale-95 transition-all animate-pop"
                            >
                                <Sparkles size={14} className="animate-pulse" /> AI Bulk Modify ({selectedIds.size})
                            </button>
                        </>
                    )}
                    <div className="bg-white p-1 rounded-2xl border border-slate-200 shadow-sm flex gap-1">
                        <button 
                            onClick={() => { setCertId('cna'); setSelectedCategory('All'); setPage(1); }} 
                            className={`px-5 py-2 rounded-xl text-[9px] font-black transition-all ${certId === 'cna' ? 'bg-slate-900 text-white shadow-md' : 'text-slate-400 hover:text-slate-600'}`}
                        >
                            CNA
                        </button>
                        {certs.filter(c => c.id !== 'cna').map(c => (
                            <button key={c.id} onClick={() => { setCertId(c.id); setSelectedCategory('All'); setPage(1); }} className={`px-5 py-2 rounded-xl text-[9px] font-black transition-all ${certId === c.id ? 'bg-slate-900 text-white shadow-md' : 'text-slate-400 hover:text-slate-600'}`}>{c.shortName}</button>
                        ))}
                    </div>
                    <button 
                        onClick={() => { setIsAiModalOpen(true); setStagedAiQuestions([]); setStatus(null); }}
                        className="px-5 py-3.5 bg-indigo-600 text-white rounded-2xl font-black text-[9px] tracking-widest flex items-center gap-2 shadow-xl border-b-4 border-indigo-800 active:scale-95 transition-all"
                    >
                        <Sparkles size={14} /> AI Generate
                    </button>
                    <button 
                        onClick={handleExport}
                        disabled={isExporting || isLoading}
                        className="px-5 py-3.5 bg-white text-slate-900 border border-slate-200 rounded-2xl font-black text-[9px] tracking-widest flex items-center gap-2 shadow-sm hover:bg-slate-50 transition-all"
                    >
                        {isExporting ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />} Export
                    </button>
                    <button 
                        onClick={() => fileInputRef.current?.click()} 
                        disabled={isBulkLoading}
                        className="px-5 py-3.5 bg-slate-950 text-white rounded-2xl font-black text-[9px] tracking-widest flex items-center gap-2 shadow-xl border-b-4 border-black active:scale-95 transition-all"
                    >
                        {isBulkLoading ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />} Bulk Ingest
                    </button>
                    <input type="file" ref={fileInputRef} onChange={handleImportFile} accept=".json" className="hidden" />
                </div>
            </div>

            {/* Tab Switches */}
            <div className="flex border-b border-slate-200 mb-6 gap-6 shrink-0">
                <button 
                    onClick={() => { setActiveTab('bank'); clearFilters(); }}
                    className={`pb-4 px-2 font-black text-xs tracking-widest relative transition-all uppercase flex items-center gap-2 ${activeTab === 'bank' ? 'text-indigo-600 border-b-4 border-indigo-600 font-extrabold' : 'text-slate-400 hover:text-slate-600'}`}
                >
                    <Database size={14} /> Question Bank Master
                </button>
                <button 
                    onClick={() => { setActiveTab('migrator'); clearFilters(); }}
                    className={`pb-4 px-2 font-black text-xs tracking-widest relative transition-all uppercase flex items-center gap-2 ${activeTab === 'migrator' ? 'text-indigo-600 border-b-4 border-indigo-600 font-extrabold' : 'text-slate-400 hover:text-slate-600'}`}
                >
                    <Sparkles className="text-amber-500 animate-pulse" size={14} /> AI Board Exam Migrator
                </button>
            </div>

            {status && (
                <div className={`p-4 rounded-3xl mb-6 flex items-center justify-between animate-pop shadow-xl border-2 ${
                    status.type === 'error' ? 'bg-rose-50 border-rose-100 text-rose-700' : 
                    status.type === 'warning' ? 'bg-amber-50 border-amber-100 text-amber-700' :
                    'bg-emerald-50 border-emerald-100 text-emerald-700'
                }`}>
                    <div className="flex items-center gap-3">
                        {status.type === 'error' ? <AlertCircle size={18}/> : status.type === 'warning' ? <AlertTriangle size={18}/> : <CheckCircle size={18}/>}
                        <span className="font-black text-xs tracking-widest">{status.text}</span>
                    </div>
                    <button onClick={() => setStatus(null)} className="p-1 hover:bg-black/5 rounded-lg"><X size={16}/></button>
                </div>
            )}

            {activeTab === 'migrator' ? (
                <div className="space-y-8 flex-1 flex flex-col min-h-0 overflow-visible">
                    {/* Quality Telemetry Scorecard row */}
                    {isStatsLoading ? (
                        <PageLoader compact message="Loading Board Quality Metrics..." />
                    ) : migratorStats ? (
                        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 shrink-0">
                            {/* Readiness Gauge (Left Col) */}
                            <div className="lg:col-span-5 bg-gradient-to-br from-slate-900 via-slate-950 to-slate-900 text-white rounded-[2rem] p-6 shadow-xl border border-white/5 relative overflow-hidden flex flex-col justify-between min-h-[220px]">
                                <div className="absolute top-0 right-0 p-8 transform translate-x-4 -translate-y-4 opacity-5 pointer-events-none">
                                    <Sparkles size={160} />
                                </div>
                                <div className="space-y-4">
                                    <div className="flex justify-between items-center">
                                        <span className="text-[10px] font-black tracking-wider text-rose-300 uppercase bg-rose-500/10 px-3 py-1 rounded-full">Telemetry Gauge</span>
                                        <span className="text-[10px] font-black tracking-widest text-slate-400">Target: 40% Hard</span>
                                    </div>
                                    
                                    {/* Calculated Hard Percent */}
                                    {(() => {
                                        const hardCount = migratorStats.distribution.Hard || 0;
                                        const totalCount = migratorStats.total || 0;
                                        const hardPct = totalCount > 0 ? Math.round((hardCount / totalCount) * 100) : 0;
                                        const neededToTarget = Math.max(0, Math.ceil(totalCount * 0.40) - hardCount);
                                        const isReady = hardPct >= 40;

                                        return (
                                            <>
                                                <div className="flex items-center gap-6">
                                                    <div className="relative flex items-center justify-center shrink-0 w-20 h-20 rounded-full border-4 border-dashed border-white/15">
                                                        <span className="text-2xl font-black">{hardPct}%</span>
                                                    </div>
                                                    <div className="space-y-1">
                                                        <h4 className="text-sm font-black tracking-tight text-white">Exam Readiness Score</h4>
                                                        <p className={`text-[10px] font-bold leading-relaxed ${isReady ? 'text-emerald-400' : 'text-amber-400'}`}>
                                                            {isReady 
                                                                ? "✅ Standards met! Ready for state-grade board simulation." 
                                                                : `⚠️ Target deficit: Upgrade ${neededToTarget} more questions to guarantee rigorous curriculum standards.`
                                                            }
                                                        </p>
                                                    </div>
                                                </div>
                                                <div className="w-full bg-white/10 rounded-full h-2.5 overflow-hidden">
                                                    <div 
                                                        className={`h-full rounded-full transition-all duration-1000 ${isReady ? 'bg-emerald-500' : 'bg-gradient-to-r from-amber-400 to-amber-500'}`} 
                                                        style={{ width: `${Math.min(100, hardPct)}%` }} 
                                                    />
                                                </div>
                                            </>
                                        );
                                    })()}
                                </div>
                            </div>

                            {/* Distibution Cards (Right Col) */}
                            <div className="lg:col-span-7 grid grid-cols-3 gap-4">
                                <div className="bg-white border border-slate-200 rounded-[2rem] p-6 flex flex-col justify-between shadow-sm">
                                    <div className="space-y-1">
                                        <span className="text-[9px] font-black text-rose-500 tracking-widest uppercase bg-rose-50 px-2 py-0.5 rounded-md">State Board</span>
                                        <p className="text-xs font-bold text-slate-400">Hard (Rigor+)</p>
                                    </div>
                                    <h3 className="text-4xl font-black text-slate-900 tracking-tight">{migratorStats.distribution.Hard || 0}</h3>
                                </div>
                                <div className="bg-white border border-slate-200 rounded-[2rem] p-6 flex flex-col justify-between shadow-sm">
                                    <div className="space-y-1">
                                        <span className="text-[9px] font-black text-indigo-500 tracking-widest uppercase bg-indigo-50 px-2 py-0.5 rounded-md">Interactive</span>
                                        <p className="text-xs font-bold text-slate-400">Medium</p>
                                    </div>
                                    <h3 className="text-4xl font-black text-slate-900 tracking-tight">{migratorStats.distribution.Medium || 0}</h3>
                                </div>
                                <div className="bg-white border border-slate-200 rounded-[2rem] p-6 flex flex-col justify-between shadow-sm">
                                    <div className="space-y-1">
                                        <span className="text-[9px] font-black text-emerald-500 tracking-widest uppercase bg-emerald-50 px-2 py-0.5 rounded-md">Elementary</span>
                                        <p className="text-xs font-bold text-slate-400">Easy</p>
                                    </div>
                                    <h3 className="text-4xl font-black text-slate-900 tracking-tight">{migratorStats.distribution.Easy || 0}</h3>
                                </div>
                            </div>
                        </div>
                    ) : null}

                    {/* Style Configurator panel */}
                    <div className="bg-slate-50 border border-slate-200 rounded-[2.5rem] p-6 flex flex-col sm:flex-row justify-between items-center gap-6 shrink-0 shadow-sm">
                        <div className="space-y-1 flex-1">
                            <h4 className="text-sm font-black text-slate-900 tracking-tight flex items-center gap-2">
                                <Settings2 size={16} className="text-indigo-600" /> Target Rigor Standard Configuration
                            </h4>
                            <p className="text-[10px] text-slate-400 font-bold leading-relaxed">
                                Select style guidelines matching certifying exam vendors. Gemini AI writes scenarios containing metrics and safety distractors.
                            </p>
                        </div>
                        <div className="flex bg-white p-1.5 border border-slate-200 rounded-2xl shadow-inner shrink-0 gap-1.5">
                            {(['Prometric', 'Credentia', 'Headmaster'] as const).map(style => (
                                <button
                                    key={style}
                                    type="button"
                                    onClick={() => setMigratorStyle(style)}
                                    className={`px-5 py-2.5 rounded-xl font-black tracking-widest text-[9px] transition-all ${migratorStyle === style ? 'bg-indigo-600 text-white shadow-md border-b-2 border-indigo-800' : 'text-slate-400 hover:text-slate-600 hover:bg-slate-50'}`}
                                >
                                    {style} Standard
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Non-Hard Questions Registry Hub (Candidates List) */}
                    <div className="bg-white rounded-[2.5rem] shadow-sm border border-slate-200 overflow-hidden flex-1 flex flex-col min-h-0">
                        {/* Search and Action Bar */}
                        {(() => {
                            const candidates = questions.filter(q => q.difficulty !== 'Hard');
                            return (
                                <>
                                    <div className="px-6 py-4 bg-slate-50/30 border-b border-slate-100 flex flex-col lg:flex-row items-center justify-between gap-4 shrink-0">
                                        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2">
                                            <h3 className="text-sm font-black text-slate-900 tracking-tight flex items-center gap-2 uppercase">
                                                <List size={14} className="text-indigo-600" /> Standard Questions Available ({candidates.length})
                                            </h3>
                                            <p className="text-[9px] font-bold text-slate-400 uppercase hidden sm:block">Easy/Medium questions eligible for State Board Upgrade</p>
                                        </div>
                                        <div className="flex gap-2 w-full lg:w-auto">
                                            <button
                                                type="button"
                                                onClick={handleBatchMigrateSelected}
                                                disabled={selectedIds.size === 0 || isBatchMigrating}
                                                className="px-6 py-3 bg-slate-950 text-white text-[9px] hover:bg-slate-950/80 font-black tracking-widest rounded-xl shadow-xl flex items-center justify-center gap-2 border-b-4 border-black disabled:opacity-40 select-none cursor-pointer"
                                            >
                                                <Sparkles size={12} className="text-amber-400" /> Batch AI Upgrade ({selectedIds.size})
                                            </button>
                                            <button onClick={loadQuestions} className="p-2.5 text-slate-400 hover:text-indigo-600 transition-all border border-slate-200 bg-white rounded-xl shadow-sm"><RefreshCcw size={14} className={isLoading ? 'animate-spin' : ''}/></button>
                                        </div>
                                    </div>

                                    {/* Candidates Table List */}
                                    <div className="flex-1 overflow-auto custom-scrollbar">
                                        <table className="w-full text-left border-collapse">
                                            <thead className="bg-slate-50/50 border-b border-slate-100 sticky top-0 z-20">
                                                <tr>
                                                    <th className="py-4 px-6 w-10 text-center">
                                                        <button 
                                                            type="button"
                                                            onClick={() => {
                                                                if (selectedIds.size === candidates.length) setSelectedIds(new Set());
                                                                else setSelectedIds(new Set(candidates.map(q => q._id)));
                                                            }} 
                                                            className="p-1.5 rounded-lg bg-slate-100 text-slate-300"
                                                        >
                                                            <Square size={14}/>
                                                        </button>
                                                    </th>
                                                    <th className="py-4 px-4 text-[9px] font-black text-slate-400 tracking-widest">Question Text</th>
                                                    <th className="py-4 px-6 text-[9px] font-black text-slate-400 tracking-widest text-center">Current Difficulty</th>
                                                    <th className="py-4 px-6 text-[9px] font-black text-slate-400 tracking-widest text-center">Domain</th>
                                                    <th className="py-4 px-8 text-right text-[9px] font-black text-slate-400 tracking-widest">Migration Action</th>
                                                </tr>
                                            </thead>
                                            <tbody className="divide-y divide-slate-50">
                                                {isLoading ? (
                                                    <tr><td colSpan={5} className="py-8"><PageLoader compact message="Scanning Candidates..." /></td></tr>
                                                ) : candidates.length === 0 ? (
                                                    <tr>
                                                        <td colSpan={5} className="py-24 text-center">
                                                            <div className="flex flex-col items-center gap-4 opacity-40">
                                                                <ShieldCheck size={48} className="text-emerald-500" />
                                                                <div>
                                                                    <p className="text-xs font-black tracking-widest text-slate-500">Perfect Registry: No sub-standard candidates found!</p>
                                                                    <p className="text-[10px] font-bold text-slate-400 mt-1 flex justify-center text-center">All current questions for {certId.toUpperCase()} are categorized as High-Rigor Hard standard.</p>
                                                                </div>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                ) : candidates.map(q => (
                                                    <tr key={q._id} className="hover:bg-slate-50 transition-colors group">
                                                        <td className="py-4 px-6 text-center">
                                                            <button type="button" onClick={() => toggleSelect(q._id)} className={`p-1.5 rounded-lg ${selectedIds.has(q._id) ? 'bg-indigo-600 text-white shadow-md' : 'bg-slate-100 text-slate-300'}`}>
                                                                {selectedIds.has(q._id) ? <CheckSquare size={14}/> : <Square size={14}/>}
                                                            </button>
                                                        </td>
                                                        <td className="py-4 px-4">
                                                            <p className="font-bold text-slate-800 text-xs line-clamp-1">"{q.text}"</p>
                                                        </td>
                                                        <td className="py-4 px-6 text-center">
                                                            <span className={`px-2.5 py-1 rounded-lg font-black text-[8px] uppercase tracking-wider ${q.difficulty === 'Easy' ? 'bg-emerald-50 text-emerald-600 border border-emerald-100' : 'bg-indigo-50 text-indigo-600 border border-indigo-100'}`}>{q.difficulty}</span>
                                                        </td>
                                                        <td className="py-4 px-6 text-center">
                                                            <span className="px-2.5 py-1 bg-slate-100 text-slate-500 rounded-lg font-black text-[8px] border border-slate-200">{q.category}</span>
                                                        </td>
                                                        <td className="py-4 px-8 text-right">
                                                            <button
                                                                type="button"
                                                                onClick={() => handleSingleEnvisionUpgrade(q)}
                                                                className="px-4 py-2 bg-gradient-to-r from-amber-500 to-amber-600 text-white rounded-lg font-black text-[8px] tracking-widest shadow-md hover:from-amber-600 hover:to-amber-700 transition-all select-none cursor-pointer uppercase flex items-center gap-1.5 ml-auto"
                                                            >
                                                                <Wand2 size={10} /> AI Elevate Rigor
                                                            </button>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </>
                            );
                        })()}

                        {/* Pagination footer matching original */}
                        <div className="px-6 py-4 bg-slate-50/50 border-t border-slate-100 flex justify-center sticky bottom-0 z-10">
                            <div className="flex items-center gap-2">
                                <button disabled={page === 1} onClick={() => setPage(page - 1)} className="px-4 py-2 rounded-xl border border-slate-200 bg-white hover:bg-slate-50 disabled:opacity-30 font-black text-[9px] flex items-center gap-2 shadow-sm"><ChevronLeft size={14}/> Prev</button>
                                <div className="px-4 py-2 bg-slate-900 text-white rounded-xl font-black text-[9px] shadow-lg">Page {page} / {Math.max(1, totalPages)}</div>
                                <button disabled={page >= totalPages} onClick={() => setPage(page + 1)} className="px-4 py-2 rounded-xl border border-slate-200 bg-white hover:bg-slate-50 disabled:opacity-30 font-black text-[9px] flex items-center gap-2 shadow-sm">Next <ChevronRight size={14}/></button>
                            </div>
                        </div>
                    </div>
                </div>
            ) : (
                <div className="bg-white rounded-[2.5rem] shadow-sm border border-slate-200 overflow-hidden flex-1 flex flex-col min-h-0">
                    <div className="px-6 py-4 bg-slate-50/30 border-b border-slate-100 flex flex-col lg:flex-row items-center gap-4">
                        <div className="relative flex-1 w-full group">
                            <Search className="absolute left-5 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
                            <input 
                                type="text" 
                                placeholder={`Search registry items...`} 
                                value={searchTerm} 
                                onChange={e => setSearchTerm(e.target.value)} 
                                className="w-full pl-12 pr-4 py-3 bg-white border border-slate-200 rounded-xl outline-none font-bold text-sm focus:ring-4 focus:ring-medical-500/5 transition-all" 
                            />
                        </div>
                        <div className="flex flex-wrap items-center gap-2">
                            <div className="flex items-center gap-2 px-3 py-2 bg-white border border-slate-200 rounded-xl">
                                <List size={14} className="text-slate-400" />
                                <select value={limit} onChange={e => {setLimit(parseInt(e.target.value)); setPage(1);}} className="bg-transparent border-none outline-none font-black text-[9px] text-slate-600 min-w-[60px]">
                                    {[20, 40, 60, 80, 100].map(v => <option key={v} value={v}>{v} Rows</option>)}
                                </select>
                            </div>
                            <div className="flex items-center gap-2 px-3 py-2 bg-white border border-slate-200 rounded-xl">
                                <Tag size={14} className="text-slate-400" />
                                <select value={selectedCategory} onChange={e => { setSelectedCategory(e.target.value); setPage(1); }} className="bg-transparent border-none outline-none font-black text-[9px] text-slate-600 min-w-[100px]">
                                    <option value="All">All Categories</option>
                                    {dbCategories.map(d => <option key={d} value={d}>{d}</option>)}
                                </select>
                            </div>
                            <div className="flex items-center gap-2 px-3 py-2 bg-white border border-slate-200 rounded-xl">
                                <ShieldCheck size={14} className="text-slate-400" />
                                <select value={selectedDifficulty} onChange={e => { setSelectedDifficulty(e.target.value); setPage(1); }} className="bg-transparent border-none outline-none font-black text-[9px] text-slate-600 min-w-[100px]">
                                    <option value="All">All Difficulties</option>
                                    <option value="Easy">Easy</option>
                                    <option value="Medium">Medium</option>
                                    <option value="Hard">Hard</option>
                                </select>
                            </div>
                            <div className="flex items-center gap-2 px-3 py-2 bg-white border border-slate-200 rounded-xl">
                                <Award size={14} className="text-slate-400" />
                                <select value={selectedVendor} onChange={e => { setSelectedVendor(e.target.value); setPage(1); }} className="bg-transparent border-none outline-none font-black text-[9px] text-slate-600 min-w-[110px]">
                                    <option value="All">All Vendors</option>
                                    <option value="Credentia">Credentia</option>
                                    <option value="Headmaster">Headmaster</option>
                                    <option value="Prometric">Prometric</option>
                                    <option value="Normal">Normal / Standard</option>
                                </select>
                            </div>
                            <button 
                                onClick={() => setIsCategoryModalOpen(true)}
                                className="p-2.5 text-slate-400 hover:text-indigo-600 transition-all border border-slate-200 rounded-xl bg-white shadow-sm"
                                title="Manage Domains"
                            >
                                <Settings size={16}/>
                            </button>
                            <button onClick={loadQuestions} className="p-2.5 text-slate-400 hover:text-indigo-600 transition-all" title="Refresh Registry"><RefreshCcw size={16} className={isLoading ? 'animate-spin' : ''}/></button>
                        </div>
                    </div>

                    <div className="flex-1 overflow-auto custom-scrollbar">
                        <table className="w-full text-left border-collapse">
                            <thead className="bg-slate-50/50 border-b border-slate-100 sticky top-0 z-20">
                                <tr>
                                    <th className="py-4 px-6 w-10 text-center">
                                        <button onClick={toggleSelectAll} className="p-1.5 rounded-lg bg-slate-100 text-slate-300">
                                            <Square size={14}/>
                                        </button>
                                    </th>
                                    <th className="py-4 px-4 text-[9px] font-black text-slate-400 tracking-widest">Logic Narrative</th>
                                    <th className="py-4 px-6 text-[9px] font-black text-slate-400 tracking-widest text-center">Domain</th>
                                    <th className="py-4 px-4 text-[9px] font-black text-slate-400 tracking-widest text-center">Difficulty</th>
                                    <th className="py-4 px-4 text-[9px] font-black text-slate-400 tracking-widest text-center">Exam Vendor</th>
                                    <th className="py-4 px-6 text-[9px] font-black text-slate-400 tracking-widest text-center">Jurisdiction</th>
                                    <th className="py-4 px-8 text-right text-[9px] font-black text-slate-400 tracking-widest">Registry Action</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-50">
                                {isLoading ? (
                                    <tr><td colSpan={7} className="py-32 text-center opacity-30 animate-pulse"><Database size={48} className="mx-auto mb-4"/> Indexing Registry...</td></tr>
                                ) : questions.length === 0 ? (
                                    <tr>
                                        <td colSpan={7} className="py-32 text-center">
                                            <div className="flex flex-col items-center gap-4 opacity-40">
                                                <HardDrive size={48} className="text-slate-300" />
                                                <div>
                                                    <p className="text-xs font-black tracking-widest">Registry Empty: {activeCollection}</p>
                                                    <button onClick={clearFilters} className="mt-4 flex items-center gap-2 px-6 py-2 bg-slate-900 text-white rounded-xl text-[9px] font-black mx-auto hover:bg-black transition-all"><Eraser size={12}/> Reset Filters</button>
                                                </div>
                                            </div>
                                        </td>
                                    </tr>
                                ) : questions.map(q => (
                                    <tr key={q._id} className="hover:bg-slate-50 transition-colors group">
                                        <td className="py-4 px-6 text-center">
                                            <button onClick={() => toggleSelect(q._id)} className={`p-1.5 rounded-lg ${selectedIds.has(q._id) ? 'bg-indigo-600 text-white shadow-md' : 'bg-slate-100 text-slate-300'}`}>
                                                {selectedIds.has(q._id) ? <CheckSquare size={14}/> : <Square size={14}/>}
                                            </button>
                                        </td>
                                        <td className="py-4 px-4">
                                            <p className="font-black text-slate-800 text-xs line-clamp-1">"{q.text}"</p>
                                        </td>
                                        <td className="py-4 px-6 text-center">
                                            <span className="px-2.5 py-1 bg-slate-100 text-slate-500 rounded-lg font-black text-[8px] border border-slate-200">{q.category}</span>
                                        </td>
                                        <td className="py-4 px-4 text-center">
                                            <span className={`px-2.5 py-1 rounded-lg font-black text-[8px] uppercase tracking-wider ${q.difficulty === 'Easy' ? 'bg-emerald-50 text-emerald-600 border border-emerald-100' : q.difficulty === 'Hard' ? 'bg-rose-50 text-rose-600 border border-rose-100' : 'bg-amber-50 text-amber-600 border border-amber-100'}`}>
                                                {q.difficulty || 'Medium'}
                                            </span>
                                        </td>
                                        <td className="py-4 px-4 text-center">
                                            <span className="px-2.5 py-1 bg-purple-50 text-purple-700 rounded-lg font-black text-[8px] border border-purple-100 uppercase">
                                                {(q.vendorTags && q.vendorTags.length > 0) ? q.vendorTags[0] : 'Normal'}
                                            </span>
                                        </td>
                                        <td className="py-4 px-6 text-center">
                                            <span className="px-2.5 py-1 bg-indigo-50 text-indigo-600 rounded-lg font-black text-[8px]">{(q.stateSpecificTags && q.stateSpecificTags.length > 0) ? q.stateSpecificTags[0] : 'Global'}</span>
                                        </td>
                                        <td className="py-4 px-8 text-right">
                                            <div className="flex items-center justify-end gap-1 opacity-0 group-hover:opacity-100 transition-all">
                                                <button onClick={() => setEditingQuestion({...q})} className="p-2 text-slate-400 hover:text-indigo-600 transition-all"><Edit size={14}/></button>
                                                <button onClick={() => { if(confirm('Purge Logic?')) StorageService.deleteQuestionsBulk(certId, [q._id]).then(loadQuestions); }} className="p-2 text-slate-300 hover:text-rose-600 transition-all"><Trash2 size={14}/></button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    <div className="px-6 py-4 bg-slate-50/50 border-t border-slate-100 flex justify-center">
                        <div className="flex items-center gap-2">
                            <button disabled={page === 1} onClick={() => setPage(page - 1)} className="px-4 py-2 rounded-xl border border-slate-200 bg-white hover:bg-slate-50 disabled:opacity-30 font-black text-[9px] flex items-center gap-2 shadow-sm"><ChevronLeft size={14}/> Prev</button>
                            <div className="px-4 py-2 bg-slate-900 text-white rounded-xl font-black text-[9px] shadow-lg">Page {page} / {Math.max(1, totalPages)}</div>
                            <button disabled={page >= totalPages} onClick={() => setPage(page + 1)} className="px-4 py-2 rounded-xl border border-slate-200 bg-white hover:bg-slate-50 disabled:opacity-30 font-black text-[9px] flex items-center gap-2 shadow-sm">Next <ChevronRight size={14}/></button>
                        </div>
                    </div>
                </div>
            )}

            {/* AI Side-by-Side Live Review Modal */}
            {activeDiffItem && (
                <div className="fixed inset-0 z-[250] bg-slate-900/95 backdrop-blur-xl flex items-start justify-center p-4 pt-10 md:pt-16 overflow-y-auto custom-scrollbar text-slate-800">
                    <div className="bg-white rounded-[2.5rem] shadow-2xl w-full max-w-6xl h-auto flex flex-col border border-indigo-100/10 my-auto overflow-hidden animate-pop">
                        {/* Header banner */}
                        <div className="p-8 border-b border-slate-100 flex justify-between items-center bg-slate-50/50 shrink-0">
                            <div className="flex items-center gap-3">
                                <div className="p-3 bg-amber-500/10 text-amber-600 rounded-2xl shadow-inner">
                                    <Wand2 size={24} className="animate-pulse" />
                                </div>
                                <div>
                                    <h3 className="text-xl font-black text-slate-900 tracking-tight">AI Board-Rigor Preview Panel</h3>
                                    <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mt-1">Upgrade style: {migratorStyle} Guidelines</p>
                                </div>
                            </div>
                            <button onClick={() => setActiveDiffItem(null)} className="p-3 text-slate-400 hover:text-slate-600 rounded-xl transition-all shadow-sm border border-slate-200 bg-white"><X size={20} /></button>
                        </div>

                        {/* Twin columns compare */}
                        <div className="p-8 grid grid-cols-1 md:grid-cols-2 gap-8 overflow-y-auto flex-1 custom-scrollbar">
                            {/* LEFT PANEL: Original Standard Item */}
                            <div className="space-y-6 border-r border-slate-100 pr-0 md:pr-8">
                                <div className="flex items-center gap-2 justify-between">
                                    <span className="text-[9px] font-black text-slate-400 bg-slate-100 border border-slate-200 px-3 py-1 rounded-full uppercase tracking-widest">Original Stand-Alone Item</span>
                                    <span className="text-[9px] font-black text-slate-400 bg-slate-100/50 px-2 py-0.5 rounded-md uppercase">{activeDiffItem.original.difficulty} Rigor</span>
                                </div>
                                
                                <div className="space-y-2">
                                    <label className="text-[9px] font-black text-slate-400 tracking-widest ml-1 uppercase">Standard Stem</label>
                                    <div className="p-5 bg-slate-50 border border-slate-200 rounded-2xl text-slate-700 font-bold text-sm leading-relaxed shadow-inner">
                                        "{activeDiffItem.original.text}"
                                    </div>
                                </div>

                                <div className="space-y-3">
                                    <label className="text-[9px] font-black text-slate-400 tracking-widest ml-1 uppercase">Original Options</label>
                                    <div className="space-y-2">
                                        {activeDiffItem.original.options?.map((opt, idx) => (
                                            <div key={idx} className={`p-4 rounded-xl border flex items-center justify-between text-xs font-bold leading-relaxed ${opt.isCorrect ? 'bg-emerald-50 border-emerald-200 text-emerald-800' : 'bg-slate-50/50 border-slate-200 text-slate-500'}`}>
                                                <span>{opt.text}</span>
                                                {opt.isCorrect && <span className="text-[8px] bg-emerald-500/10 text-emerald-600 px-2 py-0.5 rounded text-right tracking-widest font-black uppercase">Correct</span>}
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                <div className="space-y-2">
                                    <label className="text-[9px] font-black text-slate-400 tracking-widest ml-1 uppercase">Original Explanation</label>
                                    <div className="p-4 bg-slate-50 border border-slate-200 rounded-xl text-xs font-medium leading-relaxed text-slate-500">
                                        {activeDiffItem.original.explanation || '(No logic explanation provided)'}
                                    </div>
                                </div>
                            </div>

                            {/* RIGHT PANEL: Upgraded Scenario */}
                            <div className="space-y-6">
                                <div className="flex items-center gap-2 justify-between">
                                    <span className="text-[9px] font-black text-amber-600 bg-amber-500/10 px-3 py-1 rounded-full uppercase tracking-widest flex items-center gap-1">
                                        <Sparkles size={10} /> Upgraded Board Simulation
                                    </span>
                                    <span className="text-[9px] font-black text-white bg-slate-900 px-3 py-1 rounded-full uppercase tracking-widest">Hard Standard</span>
                                </div>

                                <div className="space-y-2">
                                    <label className="text-[9px] font-black text-slate-400 tracking-widest ml-1 uppercase flex justify-between">
                                        <span>Elevated Narrative</span>
                                        <span className="text-indigo-600 text-[8px] hover:underline cursor-pointer font-bold">Edit Live</span>
                                    </label>
                                    <textarea
                                        value={activeDiffItem.upgraded.text}
                                        onChange={e => {
                                            if (activeDiffItem) {
                                                setActiveDiffItem({
                                                    ...activeDiffItem,
                                                    upgraded: { ...activeDiffItem.upgraded, text: e.target.value }
                                                });
                                            }
                                        }}
                                        className="w-full p-5 bg-indigo-50/20 border border-indigo-100 rounded-2xl font-black text-sm text-indigo-950 leading-relaxed outline-none focus:bg-white focus:border-indigo-500 transition-all shadow-inner h-32 resize-none"
                                        placeholder="Review upgraded stem narrative..."
                                    />
                                </div>

                                {/* Upgraded choices mapping */}
                                <div className="space-y-3">
                                    <label className="text-[9px] font-black text-slate-400 tracking-widest ml-1 uppercase">Distractor Options Logic</label>
                                    <div className="space-y-2">
                                        {activeDiffItem.upgraded.options?.map((opt, idx) => (
                                            <div key={idx} className={`p-4 rounded-xl border flex items-center gap-3 transition-colors ${opt.isCorrect ? 'bg-emerald-50 border-emerald-300 text-emerald-950' : 'bg-slate-50 border-slate-200 hover:border-slate-300'}`}>
                                                <button
                                                    type="button"
                                                    onClick={() => {
                                                        const next = (activeDiffItem?.upgraded?.options || []).map((o, i) => ({ ...o, isCorrect: i === idx }));
                                                        setActiveDiffItem({
                                                            ...activeDiffItem,
                                                            upgraded: { ...activeDiffItem.upgraded, options: next }
                                                        });
                                                    }}
                                                    className={`w-6 h-6 rounded-md flex items-center justify-center text-xs font-black shrink-0 ${opt.isCorrect ? 'bg-emerald-500 text-white shadow-md' : 'bg-white text-slate-300 border hover:border-emerald-300'}`}
                                                >
                                                    {opt.isCorrect ? <Check size={12}/> : String.fromCharCode(65 + idx)}
                                                </button>
                                                <input
                                                    type="text"
                                                    value={opt.text}
                                                    onChange={e => {
                                                        const next = [...(activeDiffItem?.upgraded?.options || [])];
                                                        next[idx].text = e.target.value;
                                                        setActiveDiffItem({
                                                            ...activeDiffItem,
                                                            upgraded: { ...activeDiffItem.upgraded, options: next }
                                                        });
                                                    }}
                                                    className="bg-transparent border-none outline-none font-bold text-xs flex-1 text-slate-800"
                                                />
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                <div className="space-y-2">
                                    <label className="text-[9px] font-black text-slate-400 tracking-widest ml-1 uppercase">Rationalized Explanation</label>
                                    <textarea
                                        value={activeDiffItem.upgraded.explanation}
                                        onChange={e => {
                                            if (activeDiffItem) {
                                                setActiveDiffItem({
                                                    ...activeDiffItem,
                                                    upgraded: { ...activeDiffItem.upgraded, explanation: e.target.value }
                                                });
                                            }
                                        }}
                                        className="w-full p-4 bg-slate-50 border border-slate-200 rounded-2xl text-xs font-medium leading-relaxed text-slate-600 outline-none focus:bg-white focus:border-indigo-500 transition-all shadow-inner h-24 resize-none"
                                        placeholder="Step by step Clinical justification of options..."
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Modal Action Controls footer */}
                        <div className="p-8 border-t border-slate-100 bg-slate-50/50 flex flex-col sm:flex-row justify-between items-center gap-4 shrink-0">
                            <p className="text-[9px] font-black text-slate-400 uppercase tracking-wider">
                                🔒 This upgrade edits existing question ID in registry, preserving all practice logs!
                            </p>
                            <div className="flex gap-3 w-full sm:w-auto">
                                <button type="button" onClick={() => setActiveDiffItem(null)} className="flex-1 sm:flex-none px-6 py-3.5 bg-white text-slate-500 font-black text-[9px] tracking-widest rounded-xl border border-slate-200 hover:bg-slate-100 transition-all uppercase">Cancel</button>
                                <button
                                    type="button"
                                    onClick={handleSaveUpgradedQuestion}
                                    disabled={isMigratingItem}
                                    className="flex-1 sm:flex-none px-10 py-3.5 bg-indigo-600 text-white font-black text-[9px] tracking-widest rounded-xl shadow-xl hover:bg-indigo-700 border-b-4 border-indigo-800 active:scale-95 transition-all text-center flex items-center justify-center gap-2 uppercase select-none cursor-pointer"
                                >
                                    {isMigratingItem ? <Loader2 className="animate-spin" size={14}/> : <ShieldCheck size={14} />} Approve Upgrade & Deploy
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Batch Progress HUD Overlay */}
            {isBatchMigrating && batchProgress && (
                <div className="fixed inset-0 z-[300] bg-slate-950/90 backdrop-blur-md flex items-center justify-center p-6 text-white">
                    <div className="bg-slate-900 border border-slate-800 rounded-[2.5rem] p-8 max-w-xl w-full text-white space-y-6 shadow-2xl animate-pop">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                                <div className="p-2.5 bg-indigo-505 bg-indigo-500/10 text-indigo-400 rounded-xl animate-pulse">
                                    <Sparkles size={18} />
                                </div>
                                <div>
                                    <h4 className="text-sm font-black tracking-wider uppercase">Batch Upgrade in Progress</h4>
                                    <p className="text-[8px] font-bold text-slate-400 uppercase tracking-widest">Applying {migratorStyle} Rigor Standards</p>
                                </div>
                            </div>
                            <span className="text-[9px] font-black text-indigo-400 tracking-widest bg-indigo-500/10 px-3 py-1 rounded-full uppercase">
                                {batchProgress.current} / {batchProgress.total} Items
                            </span>
                        </div>

                        {/* Progress Meter */}
                        <div className="space-y-2">
                            <div className="w-full bg-slate-800 rounded-full h-3 overflow-hidden border border-slate-700">
                                <div 
                                    className="h-full bg-gradient-to-r from-indigo-500 to-indigo-400 rounded-full transition-all duration-300"
                                    style={{ width: `${Math.round((batchProgress.current / batchProgress.total) * 100)}%` }}
                                />
                            </div>
                            <div className="flex justify-between items-center text-[9px] font-black text-slate-500 uppercase tracking-widest">
                                <span>Migrating Live</span>
                                <span>{Math.round((batchProgress.current / batchProgress.total) * 100)}% Complete</span>
                            </div>
                        </div>

                        {/* Event Terminal Log console */}
                        <div className="bg-slate-950 border border-slate-800 p-4 rounded-2xl h-44 overflow-y-auto font-mono text-[9px] text-slate-400 space-y-1.5 custom-scrollbar flex flex-col justify-end">
                            {batchProgress.log.slice(-10).map((logLine, idx) => (
                                <div key={idx} className="leading-relaxed">
                                    {logLine.startsWith('✅') ? (
                                        <span className="text-emerald-400 font-bold">{logLine}</span>
                                    ) : logLine.startsWith('❌') ? (
                                        <span className="text-rose-400 font-bold">{logLine}</span>
                                    ) : (
                                        <span>{logLine}</span>
                                    )}
                                </div>
                            ))}
                        </div>

                        <div className="p-4 bg-slate-950/30 rounded-2xl border border-slate-800 flex justify-center text-[9px] font-bold text-slate-400 tracking-wide text-center leading-relaxed">
                            ⚠️ Please stand by. Do not close this panel during registry sync operations.
                        </div>
                    </div>
                </div>
            )}

            {/* DOMAIN MANAGEMENT MODAL - Positioned at top */}
            {isCategoryModalOpen && (
                <div className="fixed inset-0 z-[200] bg-slate-950/80 backdrop-blur-xl flex items-start justify-center p-4 pt-10 md:pt-16 overflow-y-auto custom-scrollbar">
                    <div className="bg-white rounded-[2.5rem] shadow-2xl w-full max-w-2xl h-auto animate-pop border border-white/10 overflow-hidden flex flex-col max-h-[90vh]">
                        <div className="p-8 border-b border-slate-100 flex justify-between items-center bg-slate-50/50 shrink-0">
                            <div>
                                <h3 className="text-xl font-black text-slate-900 tracking-tight">Manage Domains</h3>
                                <p className="text-[10px] font-black text-slate-400 mt-1">Certification: {activeCert?.shortName || certId.toUpperCase()}</p>
                            </div>
                            <button onClick={() => setIsCategoryModalOpen(false)} className="p-3 hover:bg-slate-100 rounded-2xl text-slate-400"><X size={24}/></button>
                        </div>
                        <div className="p-8 flex-1 overflow-y-auto custom-scrollbar space-y-6">
                            <div className="flex gap-2">
                                <button 
                                    onClick={handleAiSuggestCategories}
                                    disabled={isSuggestingCategories}
                                    className="flex-1 py-4 bg-indigo-600 text-white rounded-2xl font-black text-[10px] tracking-widest flex items-center justify-center gap-3 shadow-xl hover:bg-indigo-500 transition-all active:scale-95 disabled:opacity-50"
                                >
                                    {isSuggestingCategories ? <Loader2 size={16} className="animate-spin" /> : <Sparkles size={16} />}
                                    AI Suggest Domains
                                </button>
                                <button 
                                    onClick={handleAddCategory}
                                    className="px-8 py-4 bg-slate-900 text-white rounded-2xl font-black text-[10px] tracking-widest flex items-center justify-center gap-3 shadow-xl hover:bg-black transition-all active:scale-95"
                                >
                                    <Plus size={16} /> Add Manually
                                </button>
                            </div>

                            <div className="grid grid-cols-1 gap-2">
                                {(activeCert?.categories || []).map(cat => (
                                    <div key={cat} className="p-4 bg-slate-50 border border-slate-200 rounded-2xl flex items-center justify-between group hover:border-indigo-400 transition-all">
                                        <span className="font-black text-[10px] text-slate-800 truncate pr-4">{cat}</span>
                                        <button onClick={() => handleRemoveCategory(cat)} className="p-2 text-slate-300 hover:text-rose-500 transition-all opacity-0 group-hover:opacity-100"><Trash2 size={14}/></button>
                                    </div>
                                ))}
                                {(!activeCert?.categories || activeCert.categories.length === 0) && (
                                    <div className="py-12 text-center opacity-30 border-4 border-dashed border-slate-100 rounded-3xl flex flex-col items-center gap-3">
                                        <ListTodo size={40} />
                                        <p className="font-black text-xs">
                                            {certId === 'cna' ? 'Using Default CNA Domains' : `No Categories Defined for ${activeCert?.shortName || certId.toUpperCase()}`}
                                        </p>
                                    </div>
                                )}
                            </div>
                        </div>
                        <div className="p-6 bg-slate-50 border-t border-slate-100 text-center shrink-0">
                            <p className="text-[8px] font-black text-slate-400 tracking-widest">Domains are stored in the global certification registry.</p>
                        </div>
                    </div>
                </div>
            )}

            {/* AI GENERATION MODAL - Positioned at top */}
            {isAiModalOpen && (
                <div className="fixed inset-0 z-[200] bg-slate-950/80 backdrop-blur-xl flex items-start justify-center p-4 pt-10 md:pt-16 overflow-y-auto custom-scrollbar">
                    <div className="bg-white rounded-[2.5rem] shadow-2xl w-full max-w-5xl h-auto max-h-none flex flex-col animate-pop border border-white/10 overflow-hidden">
                        <div className="p-8 border-b border-slate-100 flex justify-between items-center bg-slate-50/50 shrink-0">
                            <div className="flex items-center gap-4">
                                <div className="p-3 bg-indigo-600 text-white rounded-2xl shadow-xl">
                                    <Sparkles size={24} />
                                </div>
                                <div>
                                    <h3 className="text-2xl font-black text-slate-900 tracking-tight leading-none">AI Synthesis Orchestrator</h3>
                                    <p className="text-[10px] font-black text-slate-400 mt-1 tracking-widest">Generating for {certId.toUpperCase()} Registry</p>
                                </div>
                            </div>
                            <button onClick={() => setIsAiModalOpen(false)} className="p-3 hover:bg-slate-100 rounded-2xl text-slate-400"><X size={24}/></button>
                        </div>

                        <div className="flex-1 overflow-hidden flex flex-col lg:flex-row">
                            {/* Parameters Sidebar */}
                            <div className="w-full lg:w-1/3 p-8 border-r border-slate-50 space-y-8 bg-slate-50/30 overflow-y-auto custom-scrollbar shrink-0">
                                <div className="space-y-6">
                                    <div className="space-y-3">
                                        <label className="text-[10px] font-black text-slate-400 tracking-widest ml-1 flex items-center gap-2">
                                            <Tag size={12} className="text-indigo-600" /> Target Domain
                                        </label>
                                        <select 
                                            value={aiParams.category}
                                            onChange={e => setAiParams({...aiParams, category: e.target.value})}
                                            className="w-full p-4 bg-white border border-slate-200 rounded-2xl font-bold text-[11px] outline-none shadow-sm focus:border-indigo-500 transition-colors"
                                        >
                                            {activeCategories.map(d => <option key={d} value={d}>{d}</option>)}
                                        </select>
                                    </div>

                                    <div className="space-y-3">
                                        <label className="text-[10px] font-black text-slate-400 tracking-widest flex items-center gap-2">
                                            <Zap size={12} className="text-amber-500" /> Rigor Level
                                        </label>
                                        <div className="flex bg-white p-1 rounded-2xl border border-slate-200 shadow-sm">
                                            {['Easy', 'Medium', 'Hard'].map(lvl => (
                                                <button 
                                                    key={lvl}
                                                    onClick={() => setAiParams({...aiParams, difficulty: lvl as any})}
                                                    className={`flex-1 py-2.5 rounded-xl text-[9px] font-black transition-all ${aiParams.difficulty === lvl ? 'bg-slate-900 text-white shadow-lg' : 'text-slate-400 hover:text-slate-600'}`}
                                                >
                                                    {lvl}
                                                </button>
                                            ))}
                                        </div>
                                    </div>

                                    <div className="space-y-3">
                                        <label className="text-[10px] font-black text-slate-400 tracking-widest flex items-center gap-2">
                                            <MapPin size={12} className="text-medical-600" /> Jurisdiction Context
                                        </label>
                                        <select 
                                            value={aiParams.state}
                                            onChange={e => setAiParams({...aiParams, state: e.target.value})}
                                            className="w-full p-4 bg-white border border-slate-200 rounded-2xl font-bold text-[11px] outline-none shadow-sm focus:border-medical-500 transition-colors"
                                        >
                                            <option value="Global">Universal Standard</option>
                                            {US_STATES.map(s => <option key={s.code} value={s.code}>{s.name}</option>)}
                                        </select>
                                    </div>

                                    <div className="space-y-3">
                                        <div className="flex items-center justify-between">
                                            <label className="text-[10px] font-black text-slate-400 tracking-widest flex items-center gap-2">
                                                <Activity size={12} className="text-rose-500" /> Synthesis Depth
                                            </label>
                                            <span className="text-[9px] font-bold text-slate-400">Up to 50 items</span>
                                        </div>
                                        <div className="bg-white p-4 rounded-2xl border border-slate-200 shadow-sm space-y-3">
                                            <div className="flex justify-between items-center px-1">
                                                <span className="text-[10px] font-black text-slate-500">Items to Generate</span>
                                                <div className="flex items-center gap-1.5">
                                                    <input 
                                                        type="number" 
                                                        min="1" 
                                                        max="50"
                                                        value={aiParams.count}
                                                        onChange={e => {
                                                            const val = parseInt(e.target.value);
                                                            if (!isNaN(val)) setAiParams({...aiParams, count: Math.max(1, Math.min(50, val))});
                                                        }}
                                                        className="w-14 px-2 py-1 bg-slate-50 border border-slate-200 rounded-lg text-right font-black text-sm text-indigo-600 focus:outline-none focus:border-indigo-500"
                                                    />
                                                    <span className="text-[11px] font-bold text-slate-400">items</span>
                                                </div>
                                            </div>
                                            <input 
                                                type="range" min="1" max="50" step="1"
                                                value={aiParams.count}
                                                onChange={e => setAiParams({...aiParams, count: parseInt(e.target.value)})}
                                                className="w-full h-1.5 bg-slate-100 rounded-full appearance-none cursor-pointer accent-indigo-600"
                                            />
                                            <div className="flex flex-wrap gap-1.5 pt-1">
                                                {[5, 10, 15, 20, 25, 30, 50].map((preset) => (
                                                    <button
                                                        key={preset}
                                                        type="button"
                                                        onClick={() => setAiParams({...aiParams, count: preset})}
                                                        className={`px-2.5 py-1 rounded-lg text-[10px] font-black transition-all ${
                                                            aiParams.count === preset
                                                                ? 'bg-indigo-600 text-white shadow-sm'
                                                                : 'bg-slate-50 text-slate-500 hover:bg-slate-100 hover:text-slate-800 border border-slate-100'
                                                        }`}
                                                    >
                                                        {preset}
                                                    </button>
                                                ))}
                                            </div>
                                            {aiParams.count > 15 && (
                                                <p className="text-[9px] font-medium text-indigo-700 bg-indigo-50/80 px-2.5 py-1.5 rounded-lg border border-indigo-100 leading-snug">
                                                    Multi-stream batching: {aiParams.count} questions will be synthesized across {Math.ceil(aiParams.count / 15)} concurrent AI pipelines with randomized answer order (A-D).
                                                </p>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                <button 
                                    onClick={handleGenerateAI}
                                    disabled={isAiGenerating}
                                    className="w-full py-6 bg-slate-900 text-white rounded-3xl font-black text-base tracking-[0.2em] shadow-2xl hover:bg-black transition-all active:scale-[0.98] disabled:opacity-50 border-b-4 border-black flex items-center justify-center gap-3"
                                >
                                    {isAiGenerating ? <Loader2 size={24} className="animate-spin" /> : <Sparkles size={24} />}
                                    {isAiGenerating ? 'Synthesizing...' : `Build Logic Items (${aiParams.count})`}
                                </button>
                            </div>

                            {/* Staging Area */}
                            <div className="flex-1 bg-slate-100/50 p-8 overflow-y-auto custom-scrollbar space-y-6">
                                <div className="flex justify-between items-center px-2">
                                    <h4 className="text-[10px] font-black text-slate-400 tracking-widest">Review Pipeline ({stagedAiQuestions.length})</h4>
                                    {stagedAiQuestions.length > 0 && (
                                        <button onClick={() => setStagedAiQuestions([])} className="text-[9px] font-black text-rose-500 hover:underline">Discard Batch</button>
                                    )}
                                </div>

                                {stagedAiQuestions.length === 0 ? (
                                    <div className="h-[400px] flex flex-col items-center justify-center text-center opacity-30 grayscale gap-4">
                                        <Brain size={64} strokeWidth={1} />
                                        <p className="font-black text-sm tracking-widest max-w-[200px]">Awaiting Logic Ingestion</p>
                                    </div>
                                ) : (
                                    <div className="space-y-4">
                                        {stagedAiQuestions.map((q, qIdx) => (
                                            <div key={q._id} className="bg-white rounded-3xl p-6 shadow-xl border border-slate-200 animate-slide-up group">
                                                <div className="flex justify-between items-start mb-3">
                                                    <div className="flex items-center gap-3">
                                                        <div className="w-8 h-8 rounded-xl bg-indigo-600 text-white flex items-center justify-center font-black text-xs shadow-md">
                                                            {qIdx + 1}
                                                        </div>
                                                        <span className="px-2 py-0.5 bg-indigo-50 text-indigo-600 rounded-lg text-[8px] font-black tracking-tighter border border-indigo-100">AI Generated</span>
                                                    </div>
                                                    <button 
                                                        onClick={() => setStagedAiQuestions(prev => prev.filter((_, i) => i !== qIdx))}
                                                        className="p-2 text-slate-300 hover:text-rose-500 transition-colors"
                                                    >
                                                        <Trash2 size={16}/>
                                                    </button>
                                                </div>
                                                <p className="font-black text-slate-800 text-sm md:text-base leading-snug mb-6 group-hover:text-indigo-900 transition-colors">"{q.text}"</p>
                                                
                                                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-6">
                                                    {q.options.map((opt, oIdx) => (
                                                        <div key={oIdx} className={`p-4 rounded-xl border-2 transition-all flex items-center gap-3 ${opt.isCorrect ? 'bg-emerald-50 border-emerald-500 ring-4 ring-emerald-500/5' : 'bg-slate-50 border-slate-100 group-hover:border-slate-200'}`}>
                                                            <div className={`w-7 h-7 rounded-lg flex items-center justify-center font-black text-xs shrink-0 ${opt.isCorrect ? 'bg-emerald-600 text-white shadow-sm ring-2 ring-emerald-300' : 'bg-white text-slate-400 border border-slate-200'}`}>
                                                                {String.fromCharCode(65 + oIdx)}
                                                            </div>
                                                            <span className={`text-[11px] font-bold flex-1 ${opt.isCorrect ? 'text-emerald-950 font-black' : 'text-slate-600'}`}>{opt.text}</span>
                                                            {opt.isCorrect && (
                                                                <span className="px-2 py-0.5 bg-emerald-100 text-emerald-800 rounded text-[9px] font-black uppercase tracking-wider flex items-center gap-1 shrink-0">
                                                                    <Check size={11} className="stroke-[3]" /> Correct
                                                                </span>
                                                            )}
                                                        </div>
                                                    ))}
                                                </div>

                                                <div className="p-5 bg-indigo-50/50 rounded-2xl border-2 border-dashed border-indigo-100">
                                                    <h5 className="text-[9px] font-black text-indigo-500 tracking-widest mb-2 flex items-center gap-2"><Info size={14}/> Board Rationale</h5>
                                                    <p className="text-[11px] font-medium text-indigo-900 leading-relaxed">"{q.explanation}"</p>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="p-8 border-t border-slate-100 bg-slate-50/50 flex flex-col md:flex-row justify-between items-center gap-6 shrink-0">
                            <div className="flex items-center gap-6">
                                <div className="flex items-center gap-3">
                                    <ShieldCheck className="text-emerald-500" size={24}/>
                                    <div>
                                        <p className="text-[10px] font-black text-slate-900">Registry Integrity Verified</p>
                                        <p className="text-[8px] font-bold text-slate-400 tracking-widest">Synthetic logic cross-referenced with CMS protocol</p>
                                    </div>
                                </div>
                            </div>
                            <div className="flex gap-4 w-full md:w-auto">
                                <button onClick={() => setIsAiModalOpen(false)} className="flex-1 md:flex-none px-8 py-4 font-black text-[10px] text-slate-400 hover:text-slate-900 transition-colors">Discard Drafts</button>
                                <button 
                                    onClick={handlePublishStaged}
                                    disabled={stagedAiQuestions.length === 0 || isAiGenerating}
                                    className="flex-1 md:flex-none px-14 py-4 bg-emerald-600 text-white rounded-2xl font-black text-xs tracking-widest shadow-2xl hover:bg-emerald-700 transition-all flex items-center justify-center gap-3 active:scale-95 disabled:opacity-30 border-b-4 border-emerald-900"
                                >
                                    {isAiGenerating ? <Loader2 className="animate-spin" size={20}/> : <Database size={20}/>} 
                                    Publish {stagedAiQuestions.length} Items
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* RE-ARCHITECTED EDIT MODAL - Positioned at top */}
            {editingQuestion && (
                <div className="fixed inset-0 z-[200] bg-slate-900/90 backdrop-blur-xl flex items-start justify-center p-2 md:p-6 pt-10 md:pt-16 overflow-y-auto custom-scrollbar">
                    <div className="bg-white rounded-3xl shadow-2xl w-full max-w-5xl h-auto flex flex-col animate-pop border border-white/10 my-auto">
                        {/* Compact Header */}
                        <div className="px-6 py-4 border-b border-slate-100 flex justify-between items-center bg-white sticky top-0 z-20 rounded-t-3xl shrink-0">
                            <div className="flex items-center gap-3">
                                <div className="p-2 bg-indigo-50 text-indigo-600 rounded-xl shadow-inner">
                                    <Edit size={18} />
                                </div>
                                <div>
                                    <h3 className="text-lg font-black text-slate-900 tracking-tight">Registry Node Editor</h3>
                                    <p className="text-[8px] font-black text-slate-400 tracking-widest">Jurisdiction: {activeCollection}</p>
                                </div>
                            </div>
                            <button onClick={() => setEditingQuestion(null)} className="p-2 hover:bg-rose-50 text-slate-400 hover:text-rose-500 rounded-xl transition-all"><X size={20} /></button>
                        </div>

                        <div className="p-6 md:p-8 space-y-6 flex-1 overflow-y-auto custom-scrollbar">
                            {/* Main Grid */}
                            <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
                                {/* Left Content: Stem and Rationale */}
                                <div className="lg:col-span-7 space-y-6">
                                    {/* AI Assistant: Adapt Vendor Format */}
                                    <div className="p-5 bg-gradient-to-br from-indigo-50/90 via-white to-amber-50/60 rounded-2xl border border-indigo-100 shadow-sm space-y-3.5">
                                        <div className="flex items-center justify-between">
                                            <div className="flex items-center gap-2">
                                                <Sparkles className="text-amber-500 animate-pulse" size={16} />
                                                <span className="font-black text-xs text-slate-900 tracking-tight">AI Refine & Adapt Vendor Format</span>
                                            </div>
                                            <span className="text-[8px] font-black text-indigo-700 bg-indigo-100/80 px-2.5 py-0.5 rounded-full uppercase tracking-wider">CNA Scope Active</span>
                                        </div>

                                        {/* Vendor Selector Pills */}
                                        <div className="space-y-1">
                                            <label className="text-[8px] font-black text-slate-400 tracking-widest uppercase">Certifying Vendor Standard</label>
                                            <div className="grid grid-cols-2 sm:grid-cols-4 gap-1.5 bg-slate-100/70 p-1 rounded-xl">
                                                {(['Credentia', 'Headmaster', 'Prometric', 'Normal'] as const).map(v => (
                                                    <button
                                                        key={v}
                                                        type="button"
                                                        onClick={() => setSingleVendorStyle(v)}
                                                        className={`py-1.5 px-2 rounded-lg text-[9px] font-black transition-all ${singleVendorStyle === v ? 'bg-slate-900 text-white shadow-sm' : 'text-slate-500 hover:text-slate-900'}`}
                                                    >
                                                        {v}
                                                    </button>
                                                ))}
                                            </div>
                                        </div>

                                        {/* Optional Prompt Input */}
                                        <div className="space-y-1">
                                            <input 
                                                type="text"
                                                value={singleCustomInstruction}
                                                onChange={e => setSingleCustomInstruction(e.target.value)}
                                                placeholder="Custom directive (e.g. Focus on hand hygiene & resident dignity)..."
                                                className="w-full px-3 py-2 bg-white border border-slate-200 rounded-xl font-medium text-[10px] text-slate-800 outline-none focus:border-indigo-500 transition-all"
                                            />
                                        </div>

                                        {/* Trigger Button */}
                                        <button
                                            type="button"
                                            onClick={handleAiModifySingleQuestion}
                                            disabled={isAiModifyingSingle}
                                            className="w-full py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl font-black text-[10px] tracking-widest flex items-center justify-center gap-2 shadow-md transition-all active:scale-95 disabled:opacity-50 border-b-2 border-indigo-800"
                                        >
                                            {isAiModifyingSingle ? <Loader2 size={14} className="animate-spin"/> : <Wand2 size={14}/>}
                                            {isAiModifyingSingle ? 'Generating CNA Revision...' : `Refine with AI (${singleVendorStyle} Format)`}
                                        </button>

                                        {/* Preview Card if singleAiPreview is available */}
                                        {singleAiPreview && (
                                            <div className="p-4 bg-white border-2 border-indigo-500/40 rounded-xl shadow-lg space-y-3 animate-pop">
                                                <div className="flex items-center justify-between border-b border-slate-100 pb-2">
                                                    <span className="text-[9px] font-black text-indigo-600 uppercase flex items-center gap-1">
                                                        <CheckCircle size={12}/> AI Revision Generated
                                                    </span>
                                                    <span className="text-[8px] font-black text-slate-400">ID Maintained: {singleAiPreview._id}</span>
                                                </div>
                                                <p className="font-bold text-slate-800 text-xs leading-snug">"{singleAiPreview.text}"</p>
                                                <div className="space-y-1">
                                                    {singleAiPreview.options?.map((opt, oIdx) => (
                                                        <div key={oIdx} className={`p-2 rounded-lg text-[10px] font-bold flex items-center gap-2 ${opt.isCorrect ? 'bg-emerald-50 text-emerald-900 border border-emerald-200' : 'bg-slate-50 text-slate-600'}`}>
                                                            <span className={`w-4 h-4 rounded text-[8px] flex items-center justify-center font-black ${opt.isCorrect ? 'bg-emerald-600 text-white' : 'bg-slate-200 text-slate-600'}`}>{String.fromCharCode(65 + oIdx)}</span>
                                                            <span>{opt.text}</span>
                                                        </div>
                                                    ))}
                                                </div>
                                                <p className="text-[9px] font-medium text-slate-500 bg-slate-50 p-2 rounded-lg leading-relaxed">Rationale: {singleAiPreview.explanation}</p>
                                                <div className="flex gap-2 pt-1">
                                                    <button
                                                        type="button"
                                                        onClick={() => setSingleAiPreview(null)}
                                                        className="flex-1 py-1.5 text-slate-400 hover:text-slate-700 font-black text-[9px] uppercase"
                                                    >
                                                        Discard
                                                    </button>
                                                    <button
                                                        type="button"
                                                        onClick={handleApplySingleAiRevision}
                                                        className="flex-1 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg font-black text-[9px] tracking-widest shadow-md uppercase"
                                                    >
                                                        Apply to Editor
                                                    </button>
                                                </div>
                                            </div>
                                        )}
                                    </div>

                                    <div className="space-y-2">
                                        <label className="text-[9px] font-black text-slate-400 tracking-widest ml-1">Question Stem / Patient Narrative</label>
                                        <textarea 
                                            value={editingQuestion.text} 
                                            onChange={e => setEditingQuestion({...editingQuestion, text: e.target.value})}
                                            className="w-full p-4 bg-slate-50 border border-slate-200 rounded-2xl font-bold text-sm md:text-base outline-none focus:bg-white focus:border-indigo-500 transition-all shadow-inner h-24 md:h-32 resize-none"
                                            placeholder="The clinical setup..."
                                        />
                                    </div>

                                    <div className="space-y-2">
                                        <label className="text-[9px] font-black text-slate-400 tracking-widest ml-1 flex items-center gap-2"><Brain size={12} className="text-indigo-500"/> Board Validation Rationale</label>
                                        <textarea 
                                            value={editingQuestion.explanation} 
                                            onChange={e => setEditingQuestion({...editingQuestion, explanation: e.target.value})}
                                            className="w-full p-4 bg-indigo-50/30 border border-indigo-100 rounded-2xl font-medium text-xs md:text-sm text-slate-700 outline-none focus:bg-white transition-all shadow-inner h-24 md:h-32 resize-none"
                                            placeholder="Explain the logic..."
                                        />
                                    </div>

                                    <div className="grid grid-cols-2 gap-4">
                                        <div className="space-y-2">
                                            <label className="text-[9px] font-black text-slate-400 tracking-widest ml-1">Domain Registry</label>
                                            <select 
                                                value={editingQuestion.category} 
                                                onChange={e => setEditingQuestion({...editingQuestion, category: e.target.value})}
                                                className="w-full p-3 bg-slate-50 border border-slate-200 rounded-xl font-black text-[10px] outline-none focus:ring-2 focus:ring-medical-500/20"
                                            >
                                                {Array.from(new Set([
                                                    ...(editingQuestion.category ? [editingQuestion.category] : []),
                                                    ...activeCategories,
                                                    ...dbCategories
                                                ])).filter(Boolean).map(d => <option key={d} value={d}>{d}</option>)}
                                            </select>
                                        </div>
                                        <div className="space-y-2">
                                            <label className="text-[9px] font-black text-slate-400 tracking-widest ml-1">Rigor Metric</label>
                                            <div className="flex bg-slate-100 p-1 rounded-xl gap-1">
                                                {['Easy', 'Medium', 'Hard'].map(d => (
                                                    <button key={d} onClick={() => setEditingQuestion({...editingQuestion, difficulty: d as any})} className={`flex-1 py-2 rounded-lg text-[8px] font-black transition-all ${editingQuestion.difficulty === d ? 'bg-slate-900 text-white shadow-md' : 'text-slate-400 hover:text-slate-600'}`}>{d}</button>
                                                ))}
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Right Content: Distractor Options */}
                                <div className="lg:col-span-5 space-y-4">
                                    <div className="flex justify-between items-center px-1">
                                        <label className="text-[9px] font-black text-slate-400 tracking-widest flex items-center gap-2"><ListTodo size={12} className="text-medical-600"/> Logic Options</label>
                                        <button 
                                            onClick={() => {
                                                const next = [...(editingQuestion.options || [])];
                                                next.push({ _id: `opt_${Date.now()}`, text: 'New Distractor', isCorrect: false });
                                                setEditingQuestion({...editingQuestion, options: next});
                                            }}
                                            className="text-[8px] font-black text-indigo-600 flex items-center gap-1 hover:underline"
                                        ><Plus size={10}/> Add Line</button>
                                    </div>
                                    <div className="space-y-2 max-h-[400px] overflow-y-auto pr-2 custom-scrollbar">
                                        {(editingQuestion.options || []).map((opt, idx) => (
                                            <div key={idx} className={`p-3 rounded-2xl border-2 transition-all flex items-center gap-3 ${opt.isCorrect ? 'bg-emerald-50 border-emerald-200' : 'bg-slate-50 border-slate-100 group'}`}>
                                                <button 
                                                    onClick={() => {
                                                        const next = editingQuestion.options.map((o, i) => ({...o, isCorrect: i === idx}));
                                                        setEditingQuestion({...editingQuestion, options: next});
                                                    }}
                                                    className={`w-8 h-8 rounded-lg flex items-center justify-center font-black text-xs transition-all ${opt.isCorrect ? 'bg-emerald-500 text-white shadow-md' : 'bg-white text-slate-300 border border-slate-200 hover:border-emerald-300'}`}
                                                >
                                                    {opt.isCorrect ? <CheckCircle size={14}/> : String.fromCharCode(65 + idx)}
                                                </button>
                                                <input 
                                                    type="text" 
                                                    value={opt.text} 
                                                    onChange={e => {
                                                        const next = [...editingQuestion.options];
                                                        next[idx].text = e.target.value;
                                                        setEditingQuestion({...editingQuestion, options: next});
                                                    }}
                                                    className="flex-1 bg-transparent border-none outline-none font-bold text-xs"
                                                />
                                                <button 
                                                    onClick={() => {
                                                        const next = editingQuestion.options.filter((_, i) => i !== idx);
                                                        setEditingQuestion({...editingQuestion, options: next});
                                                    }}
                                                    className="p-1.5 text-slate-300 hover:text-rose-500 opacity-0 group-hover:opacity-100 transition-all"
                                                ><Trash2 size={12}/></button>
                                            </div>
                                        ))}
                                    </div>
                                    <div className="p-4 bg-slate-50 rounded-2xl border border-slate-100 flex items-start gap-3">
                                        <Info size={14} className="text-slate-400 shrink-0 mt-0.5" />
                                        <p className="text-[9px] font-bold text-slate-400 leading-relaxed">Tap a letter to toggle the correct answer key. Only one option can be correct.</p>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Modal Footer */}
                        <div className="px-8 py-5 border-t border-slate-100 bg-slate-50/50 flex flex-col sm:flex-row justify-between items-center gap-4 rounded-b-3xl shrink-0">
                            <div className="flex items-center gap-2">
                                <ShieldCheck size={16} className="text-emerald-500" />
                                <span className="text-[8px] font-black text-slate-400">Validated Registry Update Active</span>
                            </div>
                            <div className="flex gap-3 w-full sm:w-auto">
                                <button onClick={() => { setEditingQuestion(null); setSingleAiPreview(null); }} className="flex-1 sm:flex-none px-8 py-3.5 bg-white text-slate-500 font-black text-[9px] tracking-widest rounded-xl border border-slate-200 hover:bg-slate-100 transition-all">Discard</button>
                                <button 
                                    onClick={handleSaveEdit} 
                                    disabled={isSavingEdit}
                                    className="flex-1 sm:flex-none px-12 py-3.5 bg-slate-900 text-white font-black text-[9px] tracking-widest rounded-xl shadow-xl hover:bg-slate-800 transition-all flex items-center justify-center gap-2 disabled:opacity-50 border-b-4 border-black active:scale-95"
                                >
                                    {isSavingEdit ? <Loader2 className="animate-spin" size={14}/> : <Save size={14} />} 
                                    Commit Logic
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* AI BULK QUESTION TRANSFORMER MODAL */}
            {isBulkAiModalOpen && (
                <div className="fixed inset-0 z-[220] bg-slate-950/80 backdrop-blur-xl flex items-start justify-center p-4 pt-8 md:pt-12 overflow-y-auto custom-scrollbar">
                    <div className="bg-white rounded-[2.5rem] shadow-2xl w-full max-w-5xl h-auto flex flex-col animate-pop border border-white/10 overflow-hidden my-auto max-h-[90vh]">
                        <div className="p-6 md:p-8 border-b border-slate-100 flex justify-between items-center bg-slate-50/50 shrink-0">
                            <div className="flex items-center gap-4">
                                <div className="p-3 bg-amber-500 text-slate-950 rounded-2xl shadow-xl">
                                    <Sparkles size={24} />
                                </div>
                                <div>
                                    <h3 className="text-xl md:text-2xl font-black text-slate-900 tracking-tight">AI Bulk Question Transformer</h3>
                                    <p className="text-[10px] font-black text-slate-400 mt-0.5 tracking-widest">Transforming {selectedIds.size} Selected Question Nodes (IDs Preserved)</p>
                                </div>
                            </div>
                            <button onClick={() => setIsBulkAiModalOpen(false)} className="p-3 hover:bg-slate-100 rounded-2xl text-slate-400"><X size={24}/></button>
                        </div>

                        <div className="p-6 md:p-8 space-y-6 overflow-y-auto custom-scrollbar flex-1">
                            {/* Scope Guardrail Banner */}
                            <div className="p-4 bg-indigo-50 border border-indigo-100 rounded-2xl flex items-start gap-3">
                                <Info size={18} className="text-indigo-600 shrink-0 mt-0.5" />
                                <div>
                                    <h5 className="font-black text-xs text-indigo-950">Nurse Assistant (CNA) Scope Guardrail Active</h5>
                                    <p className="text-[10px] font-medium text-indigo-800/90 leading-relaxed mt-0.5">
                                        AI logic is constrained strictly for Nursing Assistant students (ADLs, vital signs, hygiene, mobility, safety, infection control, resident rights). Complex Registered Nurse (RN) or Medical Doctor (MD) level questions (e.g. advanced pharmacology, IV titration) will NOT be generated.
                                    </p>
                                </div>
                            </div>

                            {/* Vendor Selector Grid */}
                            <div className="space-y-3">
                                <label className="text-[10px] font-black text-slate-400 tracking-widest uppercase ml-1">1. Select Certifying Exam Vendor</label>
                                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                                    {[
                                        { id: 'Credentia', title: 'Credentia', desc: 'Client-centered care, dignity, resident safety & communication.' },
                                        { id: 'Headmaster', title: 'Headmaster (D&S)', desc: 'Procedural accuracy, step sequencing & skill verification.' },
                                        { id: 'Prometric', title: 'Prometric', desc: 'Prioritization (FIRST/BEST action), infection control & rights.' },
                                        { id: 'Normal', title: 'Normal / Standard', desc: 'Standard balanced practice exam item format.' }
                                    ].map(v => (
                                        <button
                                            key={v.id}
                                            type="button"
                                            onClick={() => setBulkVendorStyle(v.id as any)}
                                            className={`p-4 rounded-2xl border-2 text-left transition-all ${bulkVendorStyle === v.id ? 'bg-slate-900 text-white border-slate-900 shadow-xl scale-[1.02]' : 'bg-slate-50 border-slate-200 text-slate-800 hover:border-slate-300'}`}
                                        >
                                            <div className="flex items-center justify-between mb-1">
                                                <span className="font-black text-xs">{v.title}</span>
                                                {bulkVendorStyle === v.id && <CheckCircle size={14} className="text-amber-400"/>}
                                            </div>
                                            <p className={`text-[9px] font-medium leading-normal ${bulkVendorStyle === v.id ? 'text-slate-300' : 'text-slate-500'}`}>{v.desc}</p>
                                        </button>
                                    ))}
                                </div>
                            </div>

                            {/* Custom Instruction Input */}
                            <div className="space-y-2">
                                <label className="text-[10px] font-black text-slate-400 tracking-widest uppercase ml-1">2. Custom Directives or Focus Prompts (Optional)</label>
                                <textarea
                                    value={bulkCustomInstruction}
                                    onChange={e => setBulkCustomInstruction(e.target.value)}
                                    placeholder="e.g. Ensure all distractor options focus on common CNA errors. Emphasize hand hygiene and resident dignity."
                                    className="w-full p-4 bg-slate-50 border border-slate-200 rounded-2xl text-xs font-medium outline-none focus:bg-white focus:border-indigo-500 transition-all h-20 resize-none"
                                />
                            </div>

                            {/* Transform Action Button */}
                            <button
                                type="button"
                                onClick={handleRunBulkAiTransformation}
                                disabled={isBulkAiTransforming || selectedIds.size === 0}
                                className="w-full py-4 bg-indigo-600 hover:bg-indigo-700 text-white rounded-2xl font-black text-xs tracking-[0.15em] uppercase shadow-xl transition-all active:scale-[0.99] disabled:opacity-50 border-b-4 border-indigo-900 flex items-center justify-center gap-3"
                            >
                                {isBulkAiTransforming ? <Loader2 size={18} className="animate-spin"/> : <Sparkles size={18}/>}
                                {isBulkAiTransforming ? 'Transforming Selected Questions with AI...' : `Transform ${selectedIds.size} Selected Questions (${bulkVendorStyle} CNA Format)`}
                            </button>

                            {/* Review Staging List if stagedBulkItems populated */}
                            {stagedBulkItems.length > 0 && (
                                <div className="space-y-4 pt-4 border-t border-slate-100">
                                    <div className="flex justify-between items-center">
                                        <h4 className="text-xs font-black text-slate-900 uppercase tracking-wider">
                                            Transformed Questions Review ({stagedBulkItems.filter(i => i.selected).length} / {stagedBulkItems.length} Selected)
                                        </h4>
                                        <button
                                            type="button"
                                            onClick={() => {
                                                const allSelected = stagedBulkItems.every(i => i.selected);
                                                setStagedBulkItems(prev => prev.map(i => ({ ...i, selected: !allSelected })));
                                            }}
                                            className="text-[9px] font-black text-indigo-600 hover:underline uppercase"
                                        >
                                            Toggle All
                                        </button>
                                    </div>

                                    <div className="space-y-4 max-h-[350px] overflow-y-auto pr-2 custom-scrollbar">
                                        {stagedBulkItems.map((item, idx) => (
                                            <div key={item.upgraded._id} className={`p-5 rounded-2xl border-2 transition-all ${item.selected ? 'bg-white border-indigo-200 shadow-md' : 'bg-slate-50 border-slate-200 opacity-60'}`}>
                                                <div className="flex items-start justify-between gap-4 mb-3">
                                                    <div className="flex items-center gap-3">
                                                        <button
                                                            type="button"
                                                            onClick={() => {
                                                                setStagedBulkItems(prev => prev.map((it, i) => i === idx ? { ...it, selected: !it.selected } : it));
                                                            }}
                                                            className={`w-6 h-6 rounded-lg flex items-center justify-center text-xs font-black transition-all ${item.selected ? 'bg-indigo-600 text-white' : 'bg-slate-200 text-slate-400'}`}
                                                        >
                                                            {item.selected ? <Check size={14}/> : idx + 1}
                                                        </button>
                                                        <span className="text-[9px] font-black text-slate-400 uppercase">ID: {item.upgraded._id}</span>
                                                        <span className="px-2 py-0.5 bg-indigo-50 text-indigo-600 rounded-md font-black text-[8px]">{item.upgraded.category}</span>
                                                    </div>
                                                    <span className="text-[8px] font-black text-amber-600 bg-amber-50 px-2 py-0.5 rounded-md border border-amber-200 uppercase">{bulkVendorStyle} Format</span>
                                                </div>

                                                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                                                    <div className="p-3 bg-slate-50 rounded-xl border border-slate-200 text-xs text-slate-500 font-medium space-y-1">
                                                        <span className="text-[8px] font-black text-slate-400 uppercase">Original Stem</span>
                                                        <p className="line-clamp-3">"{item.original.text}"</p>
                                                    </div>
                                                    <div className="p-3 bg-indigo-50/50 rounded-xl border border-indigo-100 text-xs text-indigo-950 font-bold space-y-1">
                                                        <span className="text-[8px] font-black text-indigo-600 uppercase">AI Transformed Stem</span>
                                                        <p className="line-clamp-3">"{item.upgraded.text}"</p>
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>

                        <div className="p-6 md:p-8 border-t border-slate-100 bg-slate-50/50 flex flex-col sm:flex-row justify-between items-center gap-4 shrink-0">
                            <span className="text-[9px] font-black text-slate-400 uppercase tracking-wider">
                                🔒 Committing replaces selected question records maintaining original IDs.
                            </span>
                            <div className="flex gap-3 w-full sm:w-auto">
                                <button
                                    type="button"
                                    onClick={() => setIsBulkAiModalOpen(false)}
                                    className="flex-1 sm:flex-none px-6 py-3.5 bg-white text-slate-500 font-black text-[9px] tracking-widest rounded-xl border border-slate-200 hover:bg-slate-100 transition-all uppercase"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="button"
                                    onClick={handleCommitBulkAiTransformed}
                                    disabled={isBulkAiSaving || stagedBulkItems.filter(i => i.selected).length === 0}
                                    className="flex-1 sm:flex-none px-8 py-3.5 bg-slate-900 text-white font-black text-[9px] tracking-widest rounded-xl shadow-xl hover:bg-black transition-all flex items-center justify-center gap-2 disabled:opacity-40 border-b-4 border-black uppercase"
                                >
                                    {isBulkAiSaving ? <Loader2 size={14} className="animate-spin"/> : <Save size={14}/>}
                                    Commit {stagedBulkItems.filter(i => i.selected).length} Transformed Questions
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};
